/* ==============================================================================
   Casca da aplicação — onde tudo se junta
   ==============================================================================
   Este é o componente que transforma "a API está no ar" em "o app funciona".

   O que ele orquestra
   ===================
     · dock de servidores  ·  sidebar de canais / amigos e DMs
     · lista de mensagens e composição
     · palco de chamada com múltiplas telas
     · lista de membros com menu de contexto e moderação
     · barra de controles de voz
     · telas de configuração (servidor, usuário, administração)

   Três decisões de estado que definem o comportamento
   ==================================================

   1. **Uma fonte de verdade por recurso, carregada sob demanda.**
      Guilds no login; canais ao entrar num servidor; mensagens ao abrir um
      canal; membros ao abrir a lista. Carregar tudo no boot faria o app levar
      segundos para abrir e buscar dados que a pessoa talvez não veja.

   2. **Mensagem otimista com id gerado no cliente.**
      O snowflake é gerado localmente, a mensagem aparece na hora, e a resposta
      do servidor a confirma pelo MESMO id. Sem isso, cada envio teria um atraso
      visível de ida e volta — e é por isso que o servidor aceita id do cliente.

   3. **O gateway é a fonte de eventos, não de estado inicial.**
      O estado vem do REST; o gateway aplica DELTAS. Reconstruir o estado a
      partir de eventos exigiria o histórico completo do evento zero, e o RESUME
      tem janela limitada.
   ============================================================================== */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AppLayout,
  Composer,
  ConversationHeader,
  DevicePicker,
  Dock,
  MemberList,
  MessageList,
  Sidebar,
  TopBar,
  VoiceStage,
  useDialog,
  type ChatMessage,
  type DockGuild,
  type MemberEntry,
  type MemberRole,
  type PinnedMessage,
  type SidebarChannel,
  type SidebarFriend,
  type StageParticipant,
  type StageVideo,
  type VoiceChannelTarget,
} from '@concord/ui';
import {
  FeedbackPlayer,
  GatewayClient,
  VoiceEngine,
  detectCapabilities,
  type VoiceEngineState,
} from '@concord/core';
import { Permission, SnowflakeGenerator, parsePermissions } from '@concord/shared';

import {
  createConcordApi,
  redimensionarImagem,
  type Channel,
  type Guild,
  type Member,
} from '../lib/concord.ts';
import {
  captureInviteFromUrl,
  clearPendingInvite,
  parseInviteInput,
} from '../lib/pending-invite.ts';
import { getAccessToken, type CurrentUser } from '../session.ts';
import { VoiceAudio } from '../components/VoiceAudio.tsx';
import {
  AddServerMenu,
  FriendContextMenu,
  GuildContextMenu,
  InviteCodePanel,
} from '../components/ServerMenu.tsx';
import { GuildSettings } from './GuildSettings.tsx';
import { UserSettings } from './UserSettings.tsx';
import { AdminPanel } from './AdminPanel.tsx';
import { FriendsScreen } from './FriendsScreen.tsx';
import { InviteScreen } from './InviteScreen.tsx';

/**
 * O codigo do convite e lido no CARREGAMENTO do modulo.
 *
 * Antes de o React montar qualquer coisa, e antes de o `AppShell` decidir o que
 * renderizar. Num `useEffect` a tela inicial apareceria primeiro e o convite
 * depois, com um piscar no meio -- e o `history.replaceState` que limpa a URL
 * rodaria tarde demais para impedir que um recarregamento repetisse a acao.
 */
const INVITE_FROM_URL = captureInviteFromUrl();

/** Chave do volume por pessoa em `localStorage`. */
/** Intervalo mínimo entre dois sons de entrada/saída de outra pessoa. */
const PEER_SOUND_THROTTLE_MS = 1_500;

const VOLUME_KEY = 'concord.volumes';

/** Chave dos dispositivos de audio escolhidos. */
const DEVICE_KEY = 'concord.dispositivos';

/** Chave dos amigos silenciados localmente. */
const MUTED_FRIENDS_KEY = 'concord.amigos-silenciados';

/**
 * Le os dispositivos de audio salvos.
 *
 * Guarda id E rotulo. O `deviceId` NAO e estavel entre sessoes -- o navegador o
 * regenera -- entao so o id produziria "dispositivo desconhecido" no dia
 * seguinte, caindo no padrao sem avisar. Com o rotulo, um dia se reencontra o
 * dispositivo por nome; sem ele, nem isso.
 */
function loadAudioDevices(): {
  inputId: string | null;
  inputLabel: string | null;
  outputId: string | null;
  outputLabel: string | null;
} {
  const vazio = {
    inputId: null,
    inputLabel: null,
    outputId: null,
    outputLabel: null,
  };
  try {
    const raw = window.localStorage.getItem(DEVICE_KEY);
    if (raw === null) return vazio;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return vazio;
    const o = parsed as Record<string, unknown>;
    const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
    return {
      inputId: str(o.inputId),
      inputLabel: str(o.inputLabel),
      outputId: str(o.outputId),
      outputLabel: str(o.outputLabel),
    };
  } catch {
    return vazio;
  }
}

/**
 * Le os amigos silenciados.
 *
 * Silenciar um amigo e LOCAL: nada e enviado ao servidor, e a outra pessoa nao
 * sabe. E a diferenca entre silenciar e bloquear -- bloquear e uma acao com
 * consequencia para os dois lados, e passa pelo servidor.
 */
function loadMutedFriends(): Set<string> {
  try {
    const raw = window.localStorage.getItem(MUTED_FRIENDS_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Le os volumes salvos.
 *
 * Persistidos localmente e nao no servidor de proposito: "o Joao fala alto no
 * MEU fone" e uma preferencia do dispositivo, nao da conta. O mesmo ajuste no
 * notebook e no celular provavelmente esta errado num dos dois.
 */
function loadVolumes(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(VOLUME_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return {};

    // Filtra valor por valor: um JSON corrompido nao pode fazer `element.volume`
    // receber NaN, o que lanca `IndexSizeError` e derruba o audio inteiro.
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        result[key] = Math.min(1, Math.max(0, value));
      }
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * Gerador de snowflake do cliente.
 *
 * `workerId` aleatório: duas abas do mesmo usuário precisam de ids distintos, e
 * não há coordenação entre elas. A janela de 5 minutos que o servidor aceita
 * cobre relógio levemente adiantado; colisão entre duas abas no mesmo
 * milissegundo com workerId diferente é impossível por construção do formato.
 */
const snowflakes = new SnowflakeGenerator({
  workerId: Math.floor(Math.random() * 1024),
});

type Route =
  | { kind: 'home' }
  | { kind: 'friends' }
  | { kind: 'guild'; guildId: string }
  | { kind: 'guild-settings'; guildId: string }
  | { kind: 'user-settings' }
  | { kind: 'admin' };

export interface AppShellProps {
  user: CurrentUser;
  onLogout: () => void;
}

export function AppShell({ user, onLogout }: AppShellProps) {
  /* ==========================================================================
     Cliente da API e gateway
     ========================================================================== */

  const api = useMemo(
    () =>
      createConcordApi({
        getToken: getAccessToken,
        onUnauthorized: onLogout,
      }),
    [onLogout],
  );

  const capabilities = useMemo(() => detectCapabilities(), []);

  /* ==========================================================================
     Estrangulamento do som de entra-e-sai
     ==========================================================================
     Uma reunião começando produz seis eventos em dois segundos, e seis sons
     sobrepostos não são seis avisos — são um estalo. Quando o servidor reinicia
     e todo mundo reconecta, é pior: dezenas.

     Um som a cada 1,5 s diz "há movimento na voz", que é a informação. O
     segundo, o terceiro e o quarto som do mesmo instante não acrescentam nada
     que a lista da lateral já não mostre.

     `useRef` e não estado: mudar isto não deve provocar render, e o valor é lido
     dentro de um manipulador registrado UMA vez no efeito do gateway — um
     estado seria capturado no primeiro render e nunca veria a atualização.
  ========================================================================== */
  const lastPeerSoundAt = useRef(0);

  /*
   * `onLogout` atravessa por REF, não por dependência de efeito.
   *
   * O efeito abaixo cria o WebSocket e o motor de voz. Uma prop de callback nas
   * dependências dele significa "recrie a conexão quando o pai re-renderizar",
   * que não é a intenção de ninguém — e no meio de uma chamada é uma chamada
   * derrubada.
   *
   * A ref é a forma de o efeito alcançar sempre a versão atual do callback sem
   * ser reexecutado por causa dele.
   */
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  /*
   * `reloadFriends` por REF, pelo mesmo motivo de `onLogoutRef`.
   *
   * Os ouvintes do gateway são registrados UMA vez, num efeito cujas
   * dependências são `[capabilities, user.id]`. Chamar a função diretamente
   * capturaria a versão do primeiro render — e ela fecha sobre `api`, que é
   * recriado. A ref sempre aponta para a atual.
   */
  const reloadFriendsRef = useRef<(() => Promise<void>) | null>(null);

  /*
   * Diálogos da aplicação, em vez de `window.prompt` e `window.confirm`.
   *
   * Os nativos BLOQUEIAM a thread principal — um `confirm()` aberto por dez
   * segundos durante uma chamada congela o processamento de áudio e a
   * renegociação WebRTC pelo mesmo tempo. E o navegador pode suprimir um
   * segundo diálogo seguido, devolvendo `null` como se a pessoa tivesse
   * cancelado: um cancelamento que ela não fez, indistinguível de um que fez.
   */
  const { ask, confirm } = useDialog();

  /*
   * O gateway e o motor de voz vivem em `useRef`, não em `useState`.
   *
   * Eles não são estado de render: recriar num re-render derrubaria a conexão e
   * a chamada. O ref garante uma instância por montagem, e o `useEffect` de
   * limpeza a descarta uma vez.
   */
  const gatewayRef = useRef<GatewayClient | null>(null);
  const voiceRef = useRef<VoiceEngine | null>(null);

  /*
   * Sons de retorno: uma instancia por montagem do app.
   *
   * Em `useRef` pela mesma razao do gateway -- recriar num re-render abriria um
   * `AudioContext` novo a cada vez, e o navegador limita quantos existem por
   * pagina. `useState` com inicializador tambem serviria, mas isto nunca e lido
   * durante o render, e portanto nao e estado de render.
   */
  /*
   * Espelho dos silenciados, para o handler do gateway alcançar o valor ATUAL.
   *
   * O handler é registrado uma vez e vive tanto quanto o socket. Uma leitura
   * direta do estado ficaria congelada no primeiro render — o mesmo problema que
   * `onLogoutRef` resolve, pela mesma razão.
   */
  const mutedFriendsRef = useRef<Set<string>>(new Set());

  const soundsRef = useRef<FeedbackPlayer | null>(null);
  if (soundsRef.current === null) {
    soundsRef.current = new FeedbackPlayer({ volume: 0.35 });
  }

  const [gatewayStatus, setGatewayStatus] = useState<string>('idle');
  const [voiceState, setVoiceState] = useState<VoiceEngineState | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  /* ==========================================================================
     Estado do domínio
     ========================================================================== */

  const [route, setRoute] = useState<Route>({ kind: 'home' });
  const [guilds, setGuilds] = useState<Guild[]>([]);
  const [channels, setChannels] = useState<Record<string, Channel[]>>({});
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [roles, setRoles] = useState<Record<string, MemberRole[]>>({});
  const [permissions, setPermissions] = useState<Record<string, bigint>>({});

  /*
   * O canal ativo e POR ESCOPO, e nao um valor unico.
   *
   * O bug que isso corrige: um `activeChannelId` global vazava entre contextos.
   * Estando em `#geral` do servidor "Brothers Games" e clicando no botao de
   * inicio, `activeGuildId` virava `null` mas `activeChannelId` continuava
   * apontando para `#geral`. A busca do canal caia no ramo de conversa direta,
   * que nao encontrava nada e usava o nome padrao -- produzindo exatamente o que
   * foi relatado: a barra de titulo dizendo "Conversa", a area principal
   * mostrando "#Conversa", e "Meus amigos" marcado na lateral.
   *
   * Escopo por servidor tambem preserva a selecao: voltar a um servidor devolve
   * o canal em que a pessoa estava, em vez de sempre o primeiro.
   */
  const [channelByScope, setChannelByScope] = useState<Record<string, string | null>>(
    {},
  );
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});

  /*
   * Fixadas por canal.
   *
   * Carregadas junto das mensagens, num pedido separado. Nao vem embutidas na
   * listagem de mensagens de proposito: as fixadas de um canal podem ser de
   * meses atras e nao estariam na primeira pagina -- a faixa do topo mostraria
   * apenas as que por acaso couberam no historico recente.
   */
  const [pinsByChannel, setPinsByChannel] = useState<
    Record<string, PinnedMessage[]>
  >({});

  /*
   * Quem esta em cada sala de voz, vindo dos EVENTOS do gateway.
   *
   * Antes isto era derivado de `members` (a lista do REST) mais um patch no
   * handler de `VOICE_STATE_UPDATE`. Dois problemas, os dois relatados:
   *
   *   · a lista de membros e carregada UMA vez, ao abrir o servidor. Quem entra
   *     na voz depois nao esta nela, entao nao aparecia -- so recarregando a
   *     pagina;
   *   · o patch so alcancava quem JA estava na lista. Um membro novo era
   *     ignorado em silencio.
   *
   * Agora o gateway manda o nome junto (ver `voicePeerSchema`), e este mapa e a
   * fonte de verdade da lateral. Ele nasce vazio e se enche por evento, o que e
   * o certo: nao ha nada para "recarregar".
   */
  const [voiceRoster, setVoiceRoster] = useState<
    Record<string, Array<{ userId: string; name: string }>>
  >({});

  const [friends, setFriends] = useState<SidebarFriend[]>([]);

  /*
   * Relacao de amizade por usuario, incluindo pendentes e bloqueios.
   *
   * `friends` guarda apenas os ACEITOS, porque e o que a lateral lista. O menu
   * de contexto do membro precisa de mais: ele tem de saber se ja ha pedido em
   * aberto para nao oferecer "enviar convite" a quem ja recebeu um -- o servidor
   * responderia com erro que a pessoa nao tinha como prever.
   *
   * Um mapa e nao um array: a consulta e sempre "qual a relacao com ESTE id",
   * feita uma vez por membro renderizado numa lista que pode ter centenas.
   */
  const [friendship, setFriendship] = useState<
    Record<string, 'outgoing' | 'incoming' | 'friends' | 'blocked'>
  >({});
  const [directChannels, setDirectChannels] = useState<
    Array<{ channelId: string; name: string; userId: string }>
  >([]);

  /*
   * Canal de voz que está sendo conectado.
   *
   * Entre o clique e o `VOICE_READY` passa a permissão do microfone, o
   * handshake e a coleta de ICE. Sem este estado a interface não mudava NADA
   * nesse intervalo — e a leitura natural de "cliquei e não aconteceu nada" é
   * clicar de novo, que foi exatamente o relato.
   */
  const [joiningChannelId, setJoiningChannelId] = useState<string | null>(null);

  /* ---- Volume por pessoa, e a chamada de voz fora do palco ---- */
  const [volumes, setVolumes] = useState<Record<string, number>>(loadVolumes);

  /** Amigos silenciados. LOCAL: a outra pessoa nao sabe. */
  const [mutedFriends, setMutedFriends] = useState<Set<string>>(loadMutedFriends);
  mutedFriendsRef.current = mutedFriends;

  /* ---- Convite ---- */
  const [inviteCode, setInviteCode] = useState<string | null>(INVITE_FROM_URL);
  const [generatedInvite, setGeneratedInvite] = useState<{
    url: string;
    code: string;
    expiresAt: string | null;
    maxUses: number | null;
  } | null>(null);

  /* ---- Menus flutuantes ---- */
  const [guildMenu, setGuildMenu] = useState<{
    guildId: string;
    x: number;
    y: number;
  } | null>(null);
  const [addServerMenu, setAddServerMenu] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [friendMenu, setFriendMenu] = useState<{
    friendId: string;
    x: number;
    y: number;
  } | null>(null);
  /*
   * A seção pedida viaja no estado, junto das coordenadas.
   *
   * Sem ela as duas setas abriam o mesmo painel com as duas listas — o que era
   * indistinguível de clicar na outra seta, e alto demais para caber acima da
   * barra: a lista de saída ficava fora da tela.
   */
  const [devicePicker, setDevicePicker] = useState<{
    x: number;
    y: number;
    section: 'input' | 'output';
  } | null>(null);

  /*
   * Dispositivos escolhidos, persistidos LOCALMENTE.
   *
   * "Qual microfone eu uso" é propriedade do computador, não da conta: a mesma
   * escolha no notebook e no celular está errada num dos dois. Mesma razão do
   * volume por pessoa.
   *
   * Guarda-se o `deviceId` E o rótulo, porque o `deviceId` NÃO é estável entre
   * sessões — o navegador o regenera. Sem o rótulo, a preferência de ontem volta
   * como "dispositivo desconhecido" e cai silenciosamente no padrão.
   */
  const [audioDevices, setAudioDevices] = useState<{
    inputId: string | null;
    inputLabel: string | null;
    outputId: string | null;
    outputLabel: string | null;
  }>(loadAudioDevices);

  const [asideCollapsed, setAsideCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingByChannel, setTypingByChannel] = useState<Record<string, string[]>>({});

  /* ==========================================================================
     Boot: guilds, amigos e conversas
     ========================================================================== */

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        /*
         * As três em PARALELO, e falha de uma não impede as outras.
         *
         * `allSettled` e não `all`: se a lista de amigos falhar, a pessoa ainda
         * consegue usar os servidores. Com `all`, um erro em qualquer uma
         * deixaria a tela vazia.
         */
        const [guildResult, friendResult, dmResult] = await Promise.allSettled([
          api.listGuilds(),
          api.listFriends(),
          api.listDirectChannels(),
        ]);

        if (!active) return;

        if (guildResult.status === 'fulfilled') {
          setGuilds(guildResult.value);
        } else {
          setError('Não foi possível carregar seus servidores.');
        }

        if (friendResult.status === 'fulfilled') {
          setFriends(
            friendResult.value
              .filter((f) => f.state === 'accepted')
              .map((f) => ({
                id: f.user.id,
                name: f.user.displayName ?? f.user.username,
                username: f.user.username,
                status: f.user.status,
              })),
          );

          /*
           * O mapa de relacoes tambem no boot.
           *
           * Sem isto, o menu de contexto de um membro so saberia a relacao
           * depois de a pessoa passar pela tela de amigos -- e ate lá ofereceria
           * "enviar convite" para quem ja e amigo.
           */
          const map: Record<
            string,
            'outgoing' | 'incoming' | 'friends' | 'blocked'
          > = {};
          for (const f of friendResult.value) {
            if (f.state === 'accepted') map[f.user.id] = 'friends';
            else if (f.state === 'blocked') map[f.user.id] = 'blocked';
            else if (f.state === 'pending') {
              map[f.user.id] = f.direction === 'incoming' ? 'incoming' : 'outgoing';
            }
          }
          setFriendship(map);
        }

        if (dmResult.status === 'fulfilled') {
          setDirectChannels(
            dmResult.value
              .filter((d) => d.counterpart !== null)
              .map((d) => ({
                channelId: d.channelId,
                name: d.counterpart!.displayName ?? d.counterpart!.username,
                userId: d.counterpart!.id,
              })),
          );
        }
      } catch {
        if (active) setError('Falha ao carregar os dados iniciais.');
      }
    })();
    return () => {
      active = false;
    };
  }, [api]);

  /* ==========================================================================
     Gateway
     ========================================================================== */

  useEffect(() => {
    const gateway = new GatewayClient({
      getToken: getAccessToken,
      device: {
        name:
          capabilities.platform === 'electron'
            ? `Concord Desktop (${capabilities.os})`
            : `Navegador (${capabilities.os})`,
        platform: capabilities.platform === 'electron' ? capabilities.os === 'macos' ? 'macos' : capabilities.os === 'linux' ? 'linux' : 'windows' : 'web',
        appVersion: '0.1.0',
      },
      capabilities: {
        multiScreen: capabilities.canShareScreen,
        e2ee: false,
      },
      onStatusChange: setGatewayStatus,
      onAuthFailure: () => onLogoutRef.current(),
    });

    gatewayRef.current = gateway;

    const voice = new VoiceEngine({
      gateway,
      onStateChange: setVoiceState,
      /*
       * A telemetria é enviada com `catch` silencioso.
       *
       * Falha ao reportar qualidade de chamada não deve produzir aviso na
       * interface: a pessoa está numa conversa, e um alerta sobre telemetria
       * seria ruído sobre algo que não afeta a chamada.
       */
      onStats: (samples) => {
        /*
         * O TOKEN faltava, e a rota exige `requireAuth`.
         *
         * O resultado era um 401 vermelho no console a cada 10 segundos de
         * chamada — `POST /api/v1/telemetry/call-quality 401 (Unauthorized)`.
         * Nunca quebrou a chamada, porque telemetria não participa dela, mas
         * enchia o console exatamente onde se vai procurar o erro de outra
         * coisa. Um erro recorrente que não importa treina a pessoa a ignorar
         * erros, e o próximo pode importar.
         *
         * A telemetria também nunca chegou: o `rum.call_quality` está vazio
         * desde que existe, e é ele que torna "a voz travou" diagnosticável.
         *
         * O token é lido A CADA envio, e não capturado uma vez: ele é rotativo,
         * e uma chamada longa atravessa renovações. Capturado no fechamento do
         * efeito, voltaria a dar 401 depois da primeira troca — o mesmo defeito
         * de novo, só que mais difícil de achar por ser intermitente.
         */
        const token = getAccessToken();
        if (token === null) return;

        void fetch('/api/v1/telemetry/call-quality', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ samples }),
        }).catch(() => undefined);
      },
    });

    voiceRef.current = voice;

    /* ---- Eventos de domínio ---- */
    const unsubscribes = [
      gateway.on('MESSAGE_CREATE', (data) => {
        const payload = data as {
          id: string;
          channel_id: string;
          content: string | null;
          created_at: string;
          author?: { id: string; username?: string; display_name?: string | null };
        };

        /*
         * Som de mensagem so quando ela e de OUTRA pessoa.
         *
         * A propria mensagem volta pelo gateway (e assim que a UI otimista se
         * confirma). Tocar nela faria cada envio soar, o que e o oposto de
         * informacao: a pessoa acabou de digitar, ela sabe que enviou.
         */
        if (
          payload.author?.id !== undefined &&
          payload.author.id !== user.id &&
          /*
           * Amigo silenciado não toca som.
           *
           * A ref é lida em vez do estado porque este handler é registrado UMA
           * vez, no efeito do gateway, cujas dependências são `[capabilities,
           * user.id]`. Ler `mutedFriends` direto capturaria o valor do primeiro
           * render e nunca veria uma mudança — o silêncio só valeria depois de
           * recarregar a página.
           */
          !mutedFriendsRef.current.has(payload.author.id)
        ) {
          soundsRef.current?.play('message');
        }

        setMessages((current) => {
          const list = current[payload.channel_id] ?? [];
          /*
           * Ignora se o id já existe.
           *
           * A mensagem que EU enviei chega de volta pelo gateway. Como o id foi
           * gerado no cliente, ele é o mesmo — e sem esta checagem apareceria
           * duplicada. É o mecanismo que faz a UI otimista funcionar sem
           * reconciliação.
           */
          if (list.some((m) => m.id === payload.id)) {
            return {
              ...current,
              [payload.channel_id]: list.map((m) =>
                m.id === payload.id ? { ...m, pending: false, failed: false } : m,
              ),
            };
          }

          return {
            ...current,
            [payload.channel_id]: [
              ...list,
              {
                id: payload.id,
                channelId: payload.channel_id,
                content: payload.content,
                createdAt: payload.created_at,
                editedAt: null,
                author: payload.author
                  ? {
                      id: payload.author.id,
                      username: payload.author.username ?? '',
                      displayName: payload.author.display_name ?? null,
                      nickname: null,
                    }
                  : null,
              },
            ],
          };
        });
      }),

      /* ====================================================================
         Amizade em tempo real
         ====================================================================
         O servidor publicava `FRIEND_REQUEST` e `FRIEND_UPDATE` desde sempre, e
         o cliente não escutava nenhum dos dois. Um pedido de amizade só
         aparecia recarregando a página — e ninguém recarrega para conferir se
         chegou algo que não sabe que existe.

         A reação é RECARREGAR A LISTA, e não aplicar o payload.

         Parece desperdício e não é: os eventos carregam só identificadores, e o
         que a tela precisa é o estado da relação (`pending`, `accepted`,
         `blocked`, e a direção). Reconstruir isso a partir de "fulano te mandou
         um pedido" exigiria duplicar no cliente as regras que o servidor já
         aplica — e o dia em que uma delas mudasse, as duas cópias divergiriam
         silenciosamente. `reloadFriends` refaz o mapa inteiro a partir da
         resposta do servidor, que é a única fonte que não pode estar errada.

         O custo é uma requisição por evento de amizade. São eventos raros.
      ==================================================================== */
      gateway.on('FRIEND_REQUEST', () => {
        void reloadFriendsRef.current?.();
        /*
         * Som de mensagem e não de entrada em chamada.
         *
         * Um pedido de amizade é algo que CHEGOU e espera resposta — a mesma
         * natureza de uma mensagem. O som de entrada pertence à voz, e usá-lo
         * aqui faria a pessoa olhar para a chamada.
         */
        soundsRef.current?.play('message');
      }),

      gateway.on('FRIEND_UPDATE', () => {
        void reloadFriendsRef.current?.();
      }),

      /* ====================================================================
         Canais do servidor
         ====================================================================
         Criar uma sala de voz não aparecia para mais ninguém até recarregar — o
         que é especialmente ruim num produto cuja razão de ser é a voz, e cujo
         fluxo natural é "criei a sala, entra aí".

         Aqui o payload É aplicado, ao contrário da amizade: ele traz id, tipo e
         nome, que é exatamente o que a lista da lateral desenha. Não há regra de
         negócio para duplicar.

         O `guild_id` é conferido antes: o hub publica para a guild inteira, e
         quem está com OUTRO servidor aberto receberia o evento e o aplicaria na
         lista errada.
      ==================================================================== */
      gateway.on('CHANNEL_CREATE', (data) => {
        const p = data as {
          id: string;
          guild_id: string;
          type: string;
          name: string;
          position?: number;
          parent_id?: string | null;
        };
        setChannels((current) => {
          const lista = current[p.guild_id] ?? [];
          /*
           * Ignora se o id já existe.
           *
           * Quem CRIOU o canal já o inseriu pela resposta da chamada REST, e
           * recebe o evento logo depois. Sem esta checagem, o canal apareceria
           * duas vezes para quem o criou — exatamente o mecanismo que
           * `MESSAGE_CREATE` usa pelo mesmo motivo.
           */
          if (lista.some((c) => c.id === p.id)) return current;
          return {
            ...current,
            [p.guild_id]: [
              ...lista,
              {
                id: p.id,
                name: p.name,
                type: p.type as Channel['type'],
                topic: null,
                rateLimitSeconds: 0,
                guildId: p.guild_id,
                parentId: p.parent_id ?? null,
                position: p.position ?? lista.length,
                e2ee: false,
                maxParticipants: null,
              },
            ],
          };
        });
      }),

      gateway.on('CHANNEL_UPDATE', (data) => {
        const p = data as { id: string; guild_id: string; name?: string };
        if (p.name === undefined) return;
        setChannels((current) => ({
          ...current,
          [p.guild_id]: (current[p.guild_id] ?? []).map((c) =>
            c.id === p.id ? { ...c, name: p.name as string } : c,
          ),
        }));
      }),

      gateway.on('CHANNEL_DELETE', (data) => {
        const p = data as { id: string; guild_id: string };
        setChannels((current) => ({
          ...current,
          [p.guild_id]: (current[p.guild_id] ?? []).filter((c) => c.id !== p.id),
        }));
        /*
         * Se o canal aberto era o apagado, volta para o estado sem canal.
         *
         * Sem isto a pessoa fica olhando para uma conversa que não existe mais,
         * com um compositor que devolveria 404 no primeiro envio.
         */
        setChannelByScope((current) =>
          current[p.guild_id] === p.id
            ? { ...current, [p.guild_id]: null }
            : current,
        );
      }),

      /* ====================================================================
         Identidade do servidor
         ====================================================================
         Trocar o nome, o ícone ou o banner só aparecia recarregando — e o ícone
         é usado como identificador em três lugares (dock, lateral, barra
         superior), então "mudei o logo e não mudou" é o relato inevitável.
      ==================================================================== */
      gateway.on('GUILD_UPDATE', (data) => {
        const p = data as { id: string; name?: string; icon_url?: string | null };
        setGuilds((current) =>
          current.map((g) =>
            g.id === p.id
              ? {
                  ...g,
                  ...(p.name !== undefined ? { name: p.name } : {}),
                  ...(p.icon_url !== undefined ? { iconUrl: p.icon_url } : {}),
                }
              : g,
          ),
        );
      }),

      gateway.on('TYPING_START', (data) => {
        const payload = data as { channel_id: string; user_id: string; username?: string };
        if (payload.user_id === user.id) return;

        const name = payload.username ?? 'Alguém';
        setTypingByChannel((current) => ({
          ...current,
          [payload.channel_id]: [
            ...new Set([...(current[payload.channel_id] ?? []), name]),
          ],
        }));

        /*
         * O indicador expira por conta.
         *
         * O servidor não manda "parou de digitar" — mandar exigiria detectar
         * ausência, e a ausência não gera evento. O cliente esquece depois de 6
         * segundos, que é o dobro do intervalo com que o aviso é reenviado.
         */
        setTimeout(() => {
          setTypingByChannel((current) => ({
            ...current,
            [payload.channel_id]: (current[payload.channel_id] ?? []).filter(
              (n) => n !== name,
            ),
          }));
        }, 6000);
      }),

      gateway.on('VOICE_STATE_UPDATE', (data) => {
        const payload = data as {
          channel_id: string;
          user_id: string;
          joined?: boolean;
          username?: string;
          display_name?: string | null;
        };

        /*
         * O roster da lateral e mantido AQUI, e nao derivado da lista de membros.
         *
         * `display_name ?? username` e a mesma precedencia da lista de membros,
         * menos o apelido de guild — que o gateway nao carrega. Um apelido
         * errado na lateral e melhor que nenhum nome, e o card do palco corrige
         * quando ha membro carregado.
         */
        setVoiceRoster((current) => {
          const nome =
            payload.display_name ?? payload.username ?? 'Alguém';
          const lista = current[payload.channel_id] ?? [];

          if (payload.joined === false) {
            const restante = lista.filter((p) => p.userId !== payload.user_id);
            /*
             * Canal que ficou vazio SAI do mapa.
             *
             * Deixar um array vazio faria a lateral renderizar a secao de
             * participantes de uma sala sem ninguem — e o mapa cresceria com
             * uma entrada por canal jamais visitado de novo.
             */
            if (restante.length === 0) {
              const proximo = { ...current };
              delete proximo[payload.channel_id];
              return proximo;
            }
            return { ...current, [payload.channel_id]: restante };
          }

          /*
           * Entrar SUBSTITUI por userId em vez de acrescentar.
           *
           * Duas abas da mesma pessoa emitem dois eventos com o mesmo
           * `user_id`, e a lateral lista PESSOAS, nao sessoes — acumular
           * mostraria a pessoa duas vezes na sala.
           *
           * E a pessoa sai de qualquer OUTRO canal: `voice_states` tem PK em
           * user_id, ou seja no servidor ela esta em no maximo um. Sem esta
           * limpeza, trocar de sala deixaria o nome nas duas.
           */
          const proximo: Record<string, Array<{ userId: string; name: string }>> = {};
          for (const [canal, membros] of Object.entries(current)) {
            if (canal === payload.channel_id) continue;
            const filtrado = membros.filter((p) => p.userId !== payload.user_id);
            if (filtrado.length > 0) proximo[canal] = filtrado;
          }
          proximo[payload.channel_id] = [
            ...lista.filter((p) => p.userId !== payload.user_id),
            { userId: payload.user_id, name: nome },
          ];
          return proximo;
        });

        /*
         * Som SO para os outros, e so quando eu estou na mesma sala.
         *
         * O proprio evento volta pelo gateway: sem o teste de `user_id`, entrar
         * numa sala tocaria dois sons -- o do clique e o do eco. E um evento de
         * uma sala em que eu nao estou nao me interessa: num servidor movimentado
         * seria um som a cada poucos segundos por conversas alheias.
         */
        if (payload.user_id !== user.id) {
          /*
           * O som toca TAMBÉM quando eu não estou na sala.
           *
           * Antes ele exigia que eu já estivesse na mesma chamada, e a nota
           * anterior justificava isso com o ruído de um servidor movimentado.
           * O raciocínio invertia o valor do aviso: quem já está na chamada VÊ a
           * pessoa entrar — o cartão aparece no palco, o nome entra na lista. O
           * som ali é confirmação de algo visível.
           *
           * Quem NÃO está é que precisa dele. "Alguém entrou na sala" é a
           * informação que faz a pessoa ir até lá, e ela é invisível para quem
           * está lendo um canal de texto. Era o pedido.
           *
           * O ruído continua sendo um risco real, e é o `THROTTLE` abaixo que o
           * trata — não a supressão do aviso inteiro.
           */
          const agora = Date.now();
          if (agora - lastPeerSoundAt.current > PEER_SOUND_THROTTLE_MS) {
            lastPeerSoundAt.current = agora;
            soundsRef.current?.play(
              payload.joined === false ? 'peer-leave' : 'peer-join',
            );
          }
        }
        setMembers((current) => {
          const next = { ...current };
          for (const [guildId, list] of Object.entries(next)) {
            next[guildId] = list.map((member) =>
              member.userId === payload.user_id
                ? {
                    ...member,
                    voice:
                      payload.joined === false
                        ? null
                        : (member.voice ?? {
                            channelId: payload.channel_id,
                            channelName: '',
                            selfMuted: false,
                            selfDeafened: false,
                            serverMuted: false,
                            serverDeafened: false,
                            cameraOn: false,
                            screenCount: 0,
                          }),
                  }
                : member,
            );
          }
          return next;
        });
      }),

      gateway.on('PRESENCE_UPDATE', (data) => {
        const payload = data as { user_id: string; status: string };
        setFriends((current) =>
          current.map((friend) =>
            friend.id === payload.user_id
              ? { ...friend, status: payload.status as SidebarFriend['status'] }
              : friend,
          ),
        );
      }),
    ];

    gateway.connect();

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
      void voice.dispose();
      gateway.close();
      gatewayRef.current = null;
      voiceRef.current = null;
    };
    /*
     * Sem `onLogout` nas dependências, de propósito — ver a ref acima. Também
     * sem `api`: recriar o cliente HTTP não deve derrubar o socket.
     */
  }, [capabilities, user.id]);

  /* ---- Sons descartados no desmonte do app ---- */
  useEffect(() => {
    const player = soundsRef.current;
    return () => {
      void player?.dispose();
    };
  }, []);

  /* ---- Medidor de nível, assinado separado do estado geral ---- */
  useEffect(() => {
    if (voiceState?.connected !== true) {
      setAudioLevel(0);
      return;
    }
    /*
     * O medidor tem assinatura PRÓPRIA, fora de `voiceState`.
     *
     * Ele atualiza a ~90 Hz. Passá-lo pelo estado geral faria a árvore inteira
     * re-renderizar 90 vezes por segundo durante a chamada — o que derruba o
     * quadro justamente quando a CPU já está codificando vídeo.
     */
    return voiceRef.current?.onAudioLevel((level) => setAudioLevel(level.rms));
  }, [voiceState?.connected]);

  /* ==========================================================================
     Escopo do canal ativo
     ========================================================================== */

  /*
   * `activeGuildId` e derivado aqui, ANTES de qualquer coisa que use o canal.
   *
   * Ele estava definido bem depois, junto das derivacoes de interface -- o que
   * obrigava `openChannel` e `joinVoice` a trabalhar com um estado global.
   */
  const activeGuildId =
    route.kind === 'guild' || route.kind === 'guild-settings' ? route.guildId : null;

  /** Chave do escopo atual. `'home'` cobre inicio, amigos e conversas diretas. */
  const scopeKey = activeGuildId ?? 'home';

  const activeChannelId = channelByScope[scopeKey] ?? null;

  const setActiveChannelId = useCallback(
    (channelId: string | null) => {
      setChannelByScope((current) => ({ ...current, [scopeKey]: channelId }));
    },
    [scopeKey],
  );

  /* ==========================================================================
     Carregamento sob demanda
     ========================================================================== */

  /**
   * Recarrega a lista de amigos.
   *
   * Extraido porque agora ha dois consumidores: a tela de amigos como destino do
   * inicio e a tela de amigos aberta explicitamente. Duplicar o mapeamento seria
   * duplicar o filtro de `state === 'accepted'` -- e o dia em que ele mudasse,
   * um dos dois ficaria para tras.
   */
  const reloadFriends = useCallback(async () => {
    const list = await api.listFriends();

    setFriends(
      list
        .filter((f) => f.state === 'accepted')
        .map((f) => ({
          id: f.user.id,
          name: f.user.displayName ?? f.user.username,
          username: f.user.username,
          status: f.user.status,
        })),
    );

    /*
     * O mapa e reconstruido do zero a cada recarga.
     *
     * Mesclar com o anterior manteria uma relacao que deixou de existir --
     * remover uma amizade em outra aba, por exemplo, deixaria o menu ainda
     * dizendo "ja sao amigos" aqui. Reconstruir e a unica forma de o mapa
     * refletir a resposta do servidor e nada mais.
     */
    const next: Record<string, 'outgoing' | 'incoming' | 'friends' | 'blocked'> = {};
    for (const f of list) {
      if (f.state === 'accepted') {
        next[f.user.id] = 'friends';
      } else if (f.state === 'blocked') {
        next[f.user.id] = 'blocked';
      } else if (f.state === 'pending') {
        next[f.user.id] = f.direction === 'incoming' ? 'incoming' : 'outgoing';
      }
    }
    setFriendship(next);
  }, [api]);

  reloadFriendsRef.current = reloadFriends;

  const loadGuildData = useCallback(
    async (guildId: string) => {
      try {
        const [channelList, roleList, memberList] = await Promise.all([
          api.listChannels(guildId),
          api.listRoles(guildId),
          api.listMembers(guildId),
        ]);

        setChannels((current) => ({ ...current, [guildId]: channelList }));
        setMembers((current) => ({ ...current, [guildId]: memberList }));
        setRoles((current) => ({
          ...current,
          [guildId]: roleList.map((role) => ({
            id: role.id,
            name: role.name,
            color: role.color !== null ? `#${role.color.toString(16).padStart(6, '0')}` : null,
            position: role.position,
            hoist: role.hoist,
            permissions: parsePermissions(role.permissions),
            isEveryone: role.isEveryone,
          })),
        }));

        /*
         * As permissões do próprio usuário são resolvidas no CLIENTE, com a
         * mesma função do servidor.
         *
         * Não é uma decisão de autorização — o servidor recalcula e é ele quem
         * autoriza. Aqui o cálculo serve só para esconder UI, e usar a MESMA
         * função de `@concord/shared` é o que garante que o botão que aparece é
         * o botão que funciona.
         */
        const guild = guilds.find((g) => g.id === guildId);
        const me = memberList.find((m) => m.userId === user.id);

        if (guild?.ownerId === user.id || user.instanceAdmin) {
          setPermissions((current) => ({ ...current, [guildId]: ~0n }));
        } else if (me) {
          const myRoleIds = new Set(me.roles.map((r) => r.id));
          const resolved = roleList
            .filter((role) => role.isEveryone || myRoleIds.has(role.id))
            .reduce((acc, role) => acc | parsePermissions(role.permissions), 0n);
          setPermissions((current) => ({ ...current, [guildId]: resolved }));
        }
      } catch {
        setError('Não foi possível carregar este servidor.');
      }
    },
    [api, guilds, user.id, user.instanceAdmin],
  );

  const openChannel = useCallback(
    async (channelId: string) => {
      setActiveChannelId(channelId);

      // Já carregado: não recarrega. A lista fica em memória por canal, e
      // recarregar a cada troca perderia a posição de rolagem.
      if (messages[channelId] !== undefined) return;

      try {
        const list = await api.listMessages(channelId, { limit: 50 });
        setMessages((current) => ({
          ...current,
          // O servidor devolve do mais recente para o mais antigo; a lista
          // renderiza do mais antigo para o mais recente.
          [channelId]: [...list].reverse().map(toChatMessage),
        }));
        setHasMore((current) => ({ ...current, [channelId]: list.length >= 50 }));
      } catch {
        setError('Não foi possível carregar as mensagens deste canal.');
      }

      /*
       * As fixadas vao num pedido SEPARADO, e a falha delas e silenciosa.
       *
       * Nao pode entrar no mesmo `try`: um erro ao listar fixadas mostraria
       * "nao foi possivel carregar as mensagens" sobre um canal cujas mensagens
       * carregaram perfeitamente. E a faixa de fixadas ausente e um degrade
       * aceitavel; um aviso de erro sobre ela nao e.
       */
      try {
        const pins = await api.listPins(channelId);
        setPinsByChannel((current) => ({
          ...current,
          [channelId]: pins.map((pin) => ({
            messageId: pin.messageId,
            content: pin.content,
            authorName: pin.author?.displayName ?? pin.author?.username ?? 'Alguém',
          })),
        }));
      } catch {
        // Sem faixa de fixadas neste canal. As mensagens funcionam.
      }
    },
    [api, messages, setActiveChannelId],
  );

  /* ==========================================================================
     Fixar e desafixar
     ========================================================================== */

  const togglePin = useCallback(
    async (messageId: string, pinned: boolean) => {
      if (activeChannelId === null) return;
      const channelId = activeChannelId;
      try {
        if (pinned) {
          await api.pinMessage(channelId, messageId);
        } else {
          await api.unpinMessage(channelId, messageId);
        }
        /*
         * Recarrega do servidor em vez de mexer na lista local.
         *
         * O teto de 50 fixadas e a ordem por `pinned_at` sao do servidor. Montar
         * a lista aqui exigiria replicar as duas regras -- e o dia em que o teto
         * mudasse, o cliente mostraria uma fixada que o servidor recusou.
         */
        const pins = await api.listPins(channelId);
        setPinsByChannel((current) => ({
          ...current,
          [channelId]: pins.map((pin) => ({
            messageId: pin.messageId,
            content: pin.content,
            authorName: pin.author?.displayName ?? pin.author?.username ?? 'Alguém',
          })),
        }));
      } catch (cause) {
        soundsRef.current?.play('error');
        setError(
          cause instanceof Error && cause.message.length > 0
            ? cause.message
            : 'Não foi possível fixar esta mensagem.',
        );
      }
    },
    [activeChannelId, api],
  );

  const loadOlder = useCallback(async () => {
    if (activeChannelId === null || loadingOlder) return;
    const list = messages[activeChannelId] ?? [];
    const oldest = list[0]?.id;
    if (oldest === undefined) return;

    setLoadingOlder(true);
    try {
      const older = await api.listMessages(activeChannelId, {
        before: oldest,
        limit: 50,
      });
      setMessages((current) => ({
        ...current,
        [activeChannelId]: [
          ...[...older].reverse().map(toChatMessage),
          ...(current[activeChannelId] ?? []),
        ],
      }));
      setHasMore((current) => ({ ...current, [activeChannelId]: older.length >= 50 }));
    } catch {
      setError('Não foi possível carregar mensagens anteriores.');
    } finally {
      setLoadingOlder(false);
    }
  }, [activeChannelId, loadingOlder, messages, api]);

  /* ==========================================================================
     Ações
     ========================================================================== */

  const sendMessage = useCallback(
    async (content: string) => {
      if (activeChannelId === null) return;

      /*
       * UI otimista: o id vem do cliente e a mensagem aparece antes da resposta.
       *
       * O servidor aceita esse id dentro de uma janela de 5 minutos, então a
       * confirmação que volta pelo gateway tem o MESMO id — e o handler de
       * MESSAGE_CREATE apenas remove o `pending` em vez de inserir de novo.
       */
      const id = snowflakes.next().toString();
      const optimistic: ChatMessage = {
        id,
        channelId: activeChannelId,
        content,
        createdAt: new Date().toISOString(),
        editedAt: null,
        author: {
          id: user.id,
          username: user.username,
          displayName: user.displayName,
          nickname: null,
        },
        pending: true,
      };

      setMessages((current) => ({
        ...current,
        [activeChannelId]: [...(current[activeChannelId] ?? []), optimistic],
      }));

      try {
        await api.sendMessage(activeChannelId, { content, id });
        setMessages((current) => ({
          ...current,
          [activeChannelId]: (current[activeChannelId] ?? []).map((m) =>
            m.id === id ? { ...m, pending: false } : m,
          ),
        }));
      } catch {
        // Marca como falha em vez de remover: remover apagaria o que a pessoa
        // escreveu, e ela teria de digitar de novo.
        setMessages((current) => ({
          ...current,
          [activeChannelId]: (current[activeChannelId] ?? []).map((m) =>
            m.id === id ? { ...m, pending: false, failed: true } : m,
          ),
        }));
      }
    },
    [activeChannelId, api, user],
  );

  const joinVoice = useCallback(async (channelId: string) => {
    /*
     * Marca o canal como ATIVO antes de conectar.
     *
     * Sem isto, `activeChannelId` continuava nulo ao entrar numa sala de voz —
     * o cabeçalho não mostrava o canal, e a área principal caía no estado
     * "Escolha um canal" mesmo com a pessoa dentro da chamada.
     */
    setActiveChannelId(channelId);

    /*
     * Já estar NESTE canal significa apenas trazer o palco para a frente.
     *
     * Sem esta saída, clicar no canal em que a pessoa já está derrubava a
     * chamada e a reconstruía. O caminho: `join()` reenvia VOICE_JOIN, e o
     * gateway faz `if (connection.voiceChannelId !== null) leaveCurrentVoice()`
     * antes de entrar — o que é correto para TROCAR de sala e destrutivo para
     * reentrar na mesma. Na prática: áudio cortado, `RTCPeerConnection`
     * descartadas e renegociadas com todos os pares, e um `VOICE_PEER_LEAVE`
     * seguido de `VOICE_PEER_JOIN` piscando na lista de todo mundo.
     *
     * E é justamente o clique mais natural: a pessoa está numa sala, abre um
     * canal de texto para ler algo, e clica na sala de novo para voltar a ver a
     * chamada.
     */
    if (voiceRef.current?.state.channelId === channelId) {
      return;
    }

    setJoiningChannelId(channelId);

    /*
     * O som toca ANTES de conectar, e nao depois.
     *
     * Duas razoes. A primeira e de produto: o retorno tem de vir no clique,
     * porque conectar leva de centenas de milissegundos a alguns segundos
     * (permissao do microfone, handshake, coleta de ICE) e um som que chega
     * depois disso e lido como um segundo evento, nao como confirmacao.
     *
     * A segunda e tecnica: `AudioContext` so sai do estado suspenso dentro de um
     * gesto do usuario. Depois do `await` a pilha ja nao e mais o handler de
     * clique, e o navegador recusa -- silenciosamente.
     */
    soundsRef.current?.play('join');

    try {
      await voiceRef.current?.join(channelId);
    } catch {
      soundsRef.current?.play('error');
    } finally {
      setJoiningChannelId(null);
    }
  }, [setActiveChannelId]);

  /* ==========================================================================
     Sair da chamada pede confirmação
     ==========================================================================
     Foi o pedido, e a razão dele é a geometria dos botões: o "Desligar" fica na
     barra de comando, a poucos pixels do microfone e do compartilhar tela, que
     são apertados o tempo todo durante uma chamada. Errar o alvo por 20px
     encerra a conversa — e não há como desfazer: sair derruba a malha, e voltar
     é renegociar tudo com todo mundo.

     A assimetria é o que justifica o diálogo. Confirmar à toa custa um clique;
     não confirmar custa a chamada. Todo outro controle da barra é reversível no
     mesmo gesto que o acionou — clicar de novo desfaz — e este é o único que
     não é.

     `confirmLabel` diz "Sair da chamada" e não "OK". Um diálogo cujo botão de
     confirmação não nomeia a consequência obriga a reler o título para saber o
     que se está confirmando.
  ========================================================================== */
  const leaveVoice = useCallback(async () => {
    const proceed = await confirm({
      title: 'Sair da chamada?',
      description:
        'Você deixa a sala de voz e para de ouvir e ser ouvido. Para voltar, ' +
        'entre no canal de novo.',
      confirmLabel: 'Sair da chamada',
      cancelLabel: 'Continuar na chamada',
    });
    if (!proceed) return;

    soundsRef.current?.play('leave');
    await voiceRef.current?.leave();
  }, [confirm]);

  const toggleMic = useCallback(() => {
    const engine = voiceRef.current;
    if (!engine) return;
    /*
     * O som e escolhido pelo estado que VAI valer, nao pelo atual.
     *
     * `toggleMicrophone` inverte; ler `micEnabled` depois exigiria esperar o
     * `publish` do motor chegar ao React. `!voiceState.micEnabled` e o valor
     * futuro, conhecido agora.
     */
    soundsRef.current?.play(voiceState?.micEnabled === true ? 'mute' : 'unmute');
    engine.toggleMicrophone();
  }, [voiceState?.micEnabled]);

  const shareScreen = useCallback(async () => {
    const engine = voiceRef.current;
    if (!engine) return;

    /*
     * Pergunta se cabe ANTES de abrir o seletor do sistema.
     *
     * Abrir o diálogo e depois dizer "não cabe" desperdiça o tempo de quem
     * clicou. O aviso vem com os números do governador de banda.
     */
    const check = engine.canAddScreen();
    if (!check.allowed) {
      setError(check.warning ?? 'Não é possível adicionar outra tela agora.');
      return;
    }
    if (check.warning !== null) {
      const proceed = await confirm({
        title: 'Adicionar outra tela?',
        description: check.warning,
        confirmLabel: 'Adicionar mesmo assim',
        cancelLabel: 'Deixar como está',
      });
      if (!proceed) return;
    }

    /* ======================================================================
       O áudio da tela precisa ser PEDIDO
       ======================================================================
       `withSystemAudio` ficava no padrão `false`, e ninguém o passava. O
       resultado era o relato: "tela compartilha sem áudio da janela ou tela".

       E o defeito é mais sutil do que "não chega o som". Sem `audio: true` na
       chamada, o navegador não mostra a CAIXA DE SELEÇÃO de compartilhar áudio
       no seletor do sistema — então não havia nem como pedir. A pessoa via o
       seletor, escolhia a janela, e não existia opção nenhuma de som.

       Pedir e não receber continua sendo o caso normal e não é erro: a promessa
       resolve com o vídeo mesmo sem áudio, e o suporte é irregular — no Chrome,
       aba inteira e tela inteira entregam; janela isolada frequentemente não.
       Por isso o pedido é otimista e a ausência é silenciosa.
    ====================================================================== */
    const result = await engine.addScreen({
      contentHint: 'detail',
      withSystemAudio: true,
    });
    if (result.ok) {
      soundsRef.current?.play('screen-start');
    } else if (!result.cancelled) {
      soundsRef.current?.play('error');
      setError(result.reason);
    }
    /*
     * Cancelar o seletor do sistema NAO toca nada.
     *
     * Fechar o dialogo de escolha de tela e uma decisao consciente de nao
     * compartilhar. Um som ali soaria como falha de algo que funcionou como
     * esperado.
     */
  }, [confirm]);

  const stopScreen = useCallback(async (sourceId: string) => {
    soundsRef.current?.play('screen-stop');
    await voiceRef.current?.removeScreen(sourceId);
  }, []);

  /* ==========================================================================
     Volume por pessoa
     ========================================================================== */

  /* ==========================================================================
     Som da transmissão de cada pessoa, ligado ou desligado
     ==========================================================================
     Separado do volume, e não "volume zero".

     São dois controles porque são duas decisões com durações diferentes. O
     volume é ajuste fino e reversível na mesma gesticulação; desligar o som da
     transmissão de alguém é uma escolha que dura o tempo da chamada — quem quer
     ver a tela sem o áudio do jogo não quer refazer isso a cada mudança de
     volume, e quem baixou o volume para 0 por um instante não quer perder a
     posição do controle deslizante.

     Guarda `sessionId`, e não `userId`: o alvo é a transmissão daquela sessão.
  ========================================================================== */
  const [screenAudioMuted, setScreenAudioMutedState] = useState<Set<string>>(
    () => new Set(),
  );

  const setScreenAudioMuted = useCallback((sessionId: string, muted: boolean) => {
    setScreenAudioMutedState((current) => {
      const next = new Set(current);
      if (muted) next.add(sessionId);
      else next.delete(sessionId);
      return next;
    });
  }, []);

  const setParticipantVolume = useCallback((id: string, volume: number) => {
    setVolumes((current) => {
      /*
       * O teto é 2, e não 1.
       *
       * O relato foi direto: "o microfone está muito baixo". Um par com ganho
       * baixo no sistema operacional chega abaixo do audível, e o único
       * controle disponível ia até 100% — ou seja, até "exatamente o que
       * chegou", que é justamente o volume insuficiente.
       *
       * Acima de 1 não é `element.volume` que resolve: aquela propriedade satura
       * em 1. Quem amplifica é o `GainNode` em `VoiceAudio`.
       */
      const next = { ...current, [id]: Math.min(3, Math.max(0, volume)) };
      try {
        window.localStorage.setItem(VOLUME_KEY, JSON.stringify(next));
      } catch {
        // Sem persistencia o ajuste vale para esta sessao, o que ja e o essencial.
      }
      return next;
    });
  }, []);

  /* ==========================================================================
     Ações sobre um amigo
     ========================================================================== */

  const toggleFriendMute = useCallback((friendId: string) => {
    setMutedFriends((current) => {
      const next = new Set(current);
      if (next.has(friendId)) next.delete(friendId);
      else next.add(friendId);
      try {
        window.localStorage.setItem(MUTED_FRIENDS_KEY, JSON.stringify([...next]));
      } catch {
        // Sem persistência vale para esta sessão.
      }
      return next;
    });
  }, []);

  /**
   * Liga para um amigo.
   *
   * A conversa direta É a sala de voz — o mesmo `channelId` serve de identidade
   * de sala. Foi preciso mexer no gateway para isso: ele exigia
   * `type === 'voice'` E `guild_id !== null`, o que tornava impossível ligar para
   * alguém. Conversa direta é uma sala de duas pessoas; muda só quem autoriza.
   *
   * A conversa é aberta ANTES de entrar na voz. Se a ordem fosse a inversa, o
   * palco apareceria sobre a área principal antes de o canal estar selecionado,
   * e a precedência de render mandaria de volta para a tela anterior.
   */
  const callFriend = useCallback(
    async (friendId: string) => {
      try {
        const { channelId } = await api.openDirectChannel(friendId);
        setRoute({ kind: 'home' });
        await openChannel(channelId);
        await joinVoice(channelId);
      } catch {
        soundsRef.current?.play('error');
        setError('Não foi possível iniciar a chamada.');
      }
    },
    [api, openChannel, joinVoice],
  );

  /* ==========================================================================
     Dispositivos de áudio
     ========================================================================== */

  const selectAudioDevice = useCallback(
    async (kind: 'input' | 'output', deviceId: string | null, label: string) => {
      const next =
        kind === 'input'
          ? { inputId: deviceId, inputLabel: deviceId === null ? null : label }
          : { outputId: deviceId, outputLabel: deviceId === null ? null : label };

      setAudioDevices((current) => {
        const merged = { ...current, ...next };
        try {
          window.localStorage.setItem(DEVICE_KEY, JSON.stringify(merged));
        } catch {
          // Sem persistência a escolha vale para esta sessão, o que já basta.
        }
        return merged;
      });

      /*
       * Só a ENTRADA vai para o motor.
       *
       * Trocar o microfone exige reabrir `getUserMedia` e substituir a faixa em
       * todos os pares — é o pipeline que sabe fazer isso. A SAÍDA não passa
       * pelo motor: ela é aplicada com `setSinkId` em cada elemento `<audio>`,
       * e quem os renderiza é o `VoiceAudio`. Mandar a saída para o motor seria
       * pedir a ele para configurar algo que ele não possui.
       */
      if (kind === 'input') {
        try {
          await voiceRef.current?.updateAudioSettings({ inputDeviceId: deviceId });
        } catch {
          soundsRef.current?.play('error');
          setError(
            'Não foi possível trocar o microfone. O dispositivo pode estar em ' +
              'uso por outro programa.',
          );
        }
      }
    },
    [],
  );

  /* ==========================================================================
     Imagem do servidor
     ========================================================================== */

  /**
   * Abre o seletor de arquivo, redimensiona, envia e aplica.
   *
   * O `<input type="file">` e criado e descartado a cada uso em vez de ficar
   * escondido no DOM: um input persistente guarda o arquivo anterior, e o evento
   * `change` NAO dispara ao escolher o mesmo arquivo duas vezes -- o que faz a
   * segunda tentativa de corrigir um upload errado nao fazer nada.
   */
  const pickGuildImage = useCallback(
    async (guildId: string, target: 'icon' | 'banner') => {
      const input = document.createElement('input');
      input.type = 'file';
      // A allowlist casa com o que o servidor aceita por magic byte. SVG fica
      // fora: e um documento que pode conter script.
      input.accept = 'image/png,image/jpeg,image/webp,image/gif';

      const file = await new Promise<File | null>((resolve) => {
        input.onchange = () => resolve(input.files?.[0] ?? null);
        /*
         * `oncancel` existe desde 2023 nos navegadores atuais e resolve a
         * promessa quando a pessoa fecha o seletor. Sem ele a promessa ficaria
         * pendente para sempre -- um vazamento por cada cancelamento.
         */
        input.oncancel = () => resolve(null);
        input.click();
      });

      if (file === null) return;

      try {
        /*
         * O icone e quadrado e pequeno; o banner e largo.
         *
         * 512 px cobre o maior tamanho em que o icone aparece (a previa do
         * convite) com folga para telas de alta densidade. 1280 no banner e o
         * mesmo raciocinio para uma faixa larga.
         */
        const { data } = await redimensionarImagem(file, {
          maxSide: target === 'icon' ? 512 : 1280,
        });

        const media = await api.uploadMedia({
          kind: target === 'icon' ? 'guild_icon' : 'guild_banner',
          data,
          guildId,
        });

        const updated = await api.updateGuild(guildId, {
          ...(target === 'icon'
            ? { iconMediaId: media.id }
            : { bannerMediaId: media.id }),
        });

        setGuilds((current) =>
          current.map((guild) => (guild.id === guildId ? updated : guild)),
        );
      } catch (cause) {
        soundsRef.current?.play('error');
        setError(
          cause instanceof Error && cause.message.length > 0
            ? cause.message
            : 'Nao foi possivel enviar a imagem.',
        );
      }
    },
    [api],
  );

  /* ==========================================================================
     Ingressar por codigo e gerar codigo
     ========================================================================== */

  const askForInviteCode = useCallback(async () => {
    const input = await ask({
      title: 'Entrar em um servidor',
      description:
        'Cole o codigo de convite ou o link completo. Voce vera o nome do ' +
        'servidor antes de entrar.',
      label: 'Codigo ou link',
      placeholder: 'concord.fuxar.online/convite/AbC123...',
      maxLength: 200,
      confirmLabel: 'Continuar',
      validate: (value) =>
        parseInviteInput(value) === null
          ? 'Nao reconheci um codigo de convite aqui. Confira se copiou tudo.'
          : null,
    });
    if (input === null) return;

    const code = parseInviteInput(input);
    if (code !== null) setInviteCode(code);
  }, [ask]);

  const createShareCode = useCallback(
    async (guildId: string) => {
      try {
        /*
         * Sete dias e 25 usos como padrao, sem perguntar.
         *
         * Um formulario de quatro campos antes de gerar um link e o tipo de coisa
         * que faz a pessoa desistir e mandar o nome do servidor no chat. Quem
         * quer convite permanente ou de uso unico ajusta nas configuracoes do
         * servidor, onde ha espaco para explicar cada opcao.
         */
        const invite = await api.createInvite(guildId, {
          maxUses: 25,
          expiresInSeconds: 7 * 24 * 60 * 60,
        });
        setGeneratedInvite({
          url: invite.url,
          code: invite.code,
          expiresAt: invite.expiresAt,
          maxUses: invite.maxUses,
        });
      } catch {
        soundsRef.current?.play('error');
        setError('Nao foi possivel gerar o codigo de convite.');
      }
    },
    [api],
  );

  const createChannelIn = useCallback(
    async (guildId: string, type: 'text' | 'voice') => {
      const name = await ask({
        title: type === 'voice' ? 'Nova sala de voz' : 'Novo canal de texto',
        description:
          type === 'voice'
            ? 'A sala aparece na lista e qualquer membro com permissao pode entrar.'
            : 'O canal aparece na lista para quem tem permissao de ver.',
        label: 'Nome',
        placeholder: type === 'voice' ? 'Sala 2' : 'assuntos-gerais',
        maxLength: 100,
        confirmLabel: 'Criar',
        validate: (value) =>
          value.trim().length === 0 ? 'Escolha um nome.' : null,
      });
      if (name === null) return;

      try {
        const channel = await api.createChannel(guildId, { name, type });
        setChannels((current) => ({
          ...current,
          [guildId]: [...(current[guildId] ?? []), channel],
        }));
      } catch (cause) {
        soundsRef.current?.play('error');
        setError(
          cause instanceof Error && cause.message.length > 0
            ? cause.message
            : 'Nao foi possivel criar o canal.',
        );
      }
    },
    [api, ask],
  );

  /* ==========================================================================
     Derivações para a interface
     ========================================================================== */

  const activeGuild = guilds.find((g) => g.id === activeGuildId) ?? null;
  const guildChannels = activeGuildId !== null ? (channels[activeGuildId] ?? []) : [];
  const guildMembers = activeGuildId !== null ? (members[activeGuildId] ?? []) : [];
  const guildRoles = activeGuildId !== null ? (roles[activeGuildId] ?? []) : [];
  const myPermissions = activeGuildId !== null ? (permissions[activeGuildId] ?? 0n) : 0n;

  /*
   * Meus cargos NESTA guild, para o realce de mencao de cargo.
   *
   * Sem isto, `@moderadores` nunca destaca para quem e moderador: a mensagem
   * carrega o id do CARGO mencionado, e so quem sabe os proprios cargos consegue
   * decidir se aquilo e para si.
   */
  const myRoleIds = useMemo(
    () =>
      guildMembers.find((m) => m.userId === user.id)?.roles.map((r) => r.id) ?? [],
    [guildMembers, user.id],
  );

  const activeChannel =
    guildChannels.find((c) => c.id === activeChannelId) ??
    (activeChannelId !== null
      ? {
          id: activeChannelId,
          name:
            directChannels.find((d) => d.channelId === activeChannelId)?.name ??
            'Conversa',
          type: 'dm' as const,
          topic: null,
          rateLimitSeconds: 0,
          guildId: null,
          parentId: null,
          position: 0,
          e2ee: false,
          maxParticipants: null,
        }
      : null);

  /* ==========================================================================
     Quem está do outro lado da conversa direta
     ==========================================================================
     `directChannels` diz QUAL canal é conversa com quem; `friends` diz o nome, o
     `@usuário` e o estado dessa pessoa. Os dois porque nenhum dos dois basta: a
     lista de conversas não carrega presença, e a lista de amigos não sabe qual
     canal é o de cada um.

     Nulo quando o canal aberto não é conversa direta — e é o que decide se o
     cabeçalho da conversa aparece.
  ========================================================================== */
  const dmPeer = useMemo(() => {
    if (activeGuildId !== null || activeChannelId === null) return null;
    const conversa = directChannels.find((d) => d.channelId === activeChannelId);
    if (conversa === undefined) return null;
    const amigo = friends.find((f) => f.id === conversa.userId);
    return {
      id: conversa.userId,
      name: amigo?.name ?? conversa.name,
      username: amigo?.username,
      status: amigo?.status ?? ('offline' as const),
    };
  }, [activeGuildId, activeChannelId, directChannels, friends]);


  const dockGuilds: DockGuild[] = useMemo(
    () =>
      guilds.map((guild) => ({
        id: guild.id,
        name: guild.name,
        /*
         * `iconUrl` e OMITIDO quando nulo, nao passado como null.
         *
         * Com `exactOptionalPropertyTypes`, uma prop declarada
         * `iconUrl?: string | null | undefined` aceita null -- mas se a versao do
         * componente na `packages/ui` ainda nao tiver a prop, o spread
         * condicional mantem o objeto compativel com o tipo antigo.
         */
        ...(guild.iconUrl !== null ? { iconUrl: guild.iconUrl } : {}),
      })),
    [guilds],
  );

  const sidebarChannels: SidebarChannel[] = useMemo(
    () =>
      guildChannels
        .filter((c) => c.type !== 'category')
        .map((channel) => ({
          id: channel.id,
          name: channel.name,
          type: channel.type as SidebarChannel['type'],
          e2ee: channel.e2ee,
          /*
           * `participants` é OMITIDO em canal de texto, não passado como
           * `undefined`.
           *
           * Com `exactOptionalPropertyTypes`, `participants?: T[]` significa
           * "ausente ou T[]" — e `undefined` explícito não satisfaz. O spread
           * condicional omite de verdade.
           */
          /*
           * Os participantes vem do ROSTER (eventos), com a lista de membros
           * apenas refinando o nome quando ela o tem.
           *
           * A ordem importa: o roster e o que sabe QUEM esta na sala agora; a
           * lista de membros e o que sabe o APELIDO de guild. Inverter faria
           * quem nao esta na lista desaparecer, que era o defeito.
           */
          ...(channel.type === 'voice'
            ? {
                participants: (voiceRoster[channel.id] ?? []).map((p) => {
                  const membro = guildMembers.find((m) => m.userId === p.userId);
                  return {
                    id: p.userId,
                    name:
                      membro?.nickname ??
                      membro?.displayName ??
                      membro?.username ??
                      p.name,
                  };
                }),
              }
            : {}),
        })),
    [guildChannels, guildMembers, voiceRoster],
  );

  /*
   * Quem está falando, como um CONJUNTO estável.
   *
   * O problema que isto resolve: `memberEntries` passou a depender de quem fala,
   * e depender de `voiceState` cru faria a lista inteira de membros ser
   * remapeada a cada variação de NÍVEL de áudio — algumas vezes por segundo,
   * durante a chamada toda, num `map` sobre todos os membros da guild.
   *
   * `speakingKey` é uma STRING primitiva. O `useMemo` abaixo só recalcula quando
   * o conjunto de pessoas falando muda de verdade; subir e descer de volume
   * dentro da mesma fala não mexe nele. `sort()` porque a ordem dos
   * participantes não é estável e sem ele a string mudaria sem o conjunto mudar.
   */
  const speakingKey = (voiceState?.participants ?? [])
    .filter((p) => !p.isSelf && p.speaking)
    .map((p) => p.userId)
    .sort()
    .join(',');

  const speakingUserIds = useMemo(
    () => new Set(speakingKey.length > 0 ? speakingKey.split(',') : []),
    [speakingKey],
  );

  const memberEntries: MemberEntry[] = useMemo(
    () =>
      guildMembers.map((member) => ({
        userId: member.userId,
        displayName: member.nickname ?? member.displayName ?? member.username,
        username: member.username,
        nickname: member.nickname,
        status: member.status,
        presenceText: member.customStatus,
        isOwner: member.isOwner,
        isBot: member.isBot,
        /*
         * `'none'` quando nao ha entrada no mapa.
         *
         * Ausencia de relacao E uma relacao conhecida: significa "da para
         * enviar pedido". Deixar `undefined` esconderia a acao justamente no
         * caso mais comum, que e alguem que ainda nao e amigo.
         *
         * Eu mesmo nunca aparece: o menu ja filtra por `isSelf`.
         */
        friendship: friendship[member.userId] ?? 'none',
        roles: member.roles.map((role) => ({
          id: role.id,
          name: role.name,
          color:
            role.color !== null ? `#${role.color.toString(16).padStart(6, '0')}` : null,
          position: role.position,
          hoist: role.hoist,
          permissions:
            guildRoles.find((r) => r.id === role.id)?.permissions ?? 0n,
          isEveryone: role.isEveryone,
        })),
        voice: member.voice
          ? {
              channelId: member.voice.channelId,
              channelName:
                guildChannels.find((c) => c.id === member.voice!.channelId)?.name ??
                member.voice.channelName,
              selfMuted: member.voice.selfMuted,
              selfDeafened: member.voice.selfDeafened,
              serverMuted: member.voice.serverMuted,
              serverDeafened: member.voice.serverDeafened,
              /*
               * Aqui a chave é o USUÁRIO, não a sessão.
               *
               * A lista de membros não conhece sessões — ela lista pessoas do
               * servidor, e a pessoa pode estar em chamada por qualquer uma das
               * abas dela. `some` cobre isso: se QUALQUER sessão daquela pessoa
               * está falando, ela aparece falando na lista.
               */
              speaking: speakingUserIds.has(member.userId),
              screenCount: member.voice.screenCount,
              cameraOn: member.voice.cameraOn,
            }
          : null,
      })),
    [guildMembers, guildRoles, guildChannels, speakingUserIds, friendship],
  );

  const voiceChannelTargets: VoiceChannelTarget[] = useMemo(
    () =>
      guildChannels
        .filter((c) => c.type === 'voice')
        .map((channel) => ({
          id: channel.id,
          name: channel.name,
          userLimit: channel.maxParticipants,
          occupancy: guildMembers.filter((m) => m.voice?.channelId === channel.id).length,
        })),
    [guildChannels, guildMembers],
  );

  /* ---- Palco de vídeo ---- */
  const stageParticipants: StageParticipant[] = useMemo(() => {
    if (voiceState === null) return [];
    return voiceState.participants.map((participant) => {
      const member = guildMembers.find((m) => m.userId === participant.userId);
      return {
        userId: participant.isSelf ? user.id : participant.userId,
        sessionId: participant.sessionId,
        displayName: participant.isSelf
          ? (user.displayName ?? user.username)
          : /*
             * O nome do ROSTER e o penultimo recurso, antes de "Participante".
             *
             * O roster carrega nome desde que o gateway passou a mandar. Antes,
             * quem nao estava na lista de membros carregada caia direto em
             * "Participante" — e a lista e carregada uma vez, ao abrir o
             * servidor, entao isso acontecia com qualquer pessoa que entrasse
             * depois.
             */
            (member?.nickname ??
              member?.displayName ??
              member?.username ??
              participant.username ??
              participant.displayName ??
              'Participante'),
        status: member?.status ?? 'online',
        isSelf: participant.isSelf,
        muted: participant.selfMuted,
        deafened: participant.selfDeafened,
        /*
         * `speaking` vem do MOTOR, e até agora era `false` fixo aqui.
         *
         * O campo existia no tipo e o palco já sabia desenhar o anel — nada
         * nunca o preenchia, então o anel nunca acendeu para ninguém. Para
         * pares o valor vem do `audioLevel` do cabeçalho RTP; para si mesmo, do
         * noise gate do próprio pipeline. Ver `packages/core/src/webrtc/
         * speaking.ts`.
         */
        speaking: participant.speaking,
        unstable: participant.unstable,
        connectionState: participant.connectionState,
        /*
         * O volume acompanha o participante para o palco poder mostrar o
         * controle com o valor certo. Chaveado por SESSAO, nao por usuario: duas
         * abas da mesma pessoa sao duas fontes de audio distintas.
         */
        volume: volumes[participant.sessionId] ?? 1,
      };
    });
  }, [voiceState, guildMembers, user, volumes]);

  const stageVideos: StageVideo[] = useMemo(() => {
    if (voiceState === null) return [];

    const videos: StageVideo[] = [];

    /*
     * A PRÓPRIA câmera, com o id de sessão REAL.
     *
     * O card do próprio usuário mostrava as iniciais com a webcam acesa: a faixa
     * local nunca entrava nesta lista. O palco casa vídeo com card comparando
     * `ownerSessionId === participant.sessionId`, e o participante "eu" tem o id
     * de sessão de verdade — então usar `'self'` aqui, como as telas fazem, NÃO
     * casaria com card nenhum.
     *
     * As telas locais podem usar `'self'` porque elas não vivem num card: elas
     * aparecem como miniaturas no cabeçalho e no palco de foco, e o palco já
     * traduz `isLocal` para o selo de "transmitindo" do card certo. A câmera é
     * diferente — ela PREENCHE o card.
     */
    const selfSessionId =
      voiceState.participants.find((p) => p.isSelf)?.sessionId ?? null;

    if (voiceState.camera !== null && selfSessionId !== null) {
      videos.push({
        id: voiceState.camera.sourceId,
        stream: new MediaStream([voiceState.camera.track]),
        kind: 'camera',
        label: 'Sua câmera',
        ownerSessionId: selfSessionId,
        ownerName: 'Você',
        // `isLocal` é o que faz o `VideoSurface` espelhar. Ver o comentário lá.
        isLocal: true,
      });
    }

    // Telas locais: quem compartilha precisa VER o que está compartilhando,
    // senão não sabe se escolheu a janela certa.
    for (const screen of voiceState.screens) {
      if (screen.purpose !== 'screen') continue;
      videos.push({
        id: screen.sourceId,
        stream: new MediaStream([screen.track]),
        kind: 'screen',
        label: screen.label,
        ownerSessionId: 'self',
        ownerName: 'Você',
        isLocal: true,
      });
    }

    for (const participant of voiceState.participants) {
      if (participant.isSelf) continue;
      const member = guildMembers.find((m) => m.userId === participant.userId);
      const name = member?.nickname ?? member?.displayName ?? member?.username ?? 'Par';

      for (const track of participant.tracks) {
        if (track.purpose === 'microphone') continue;
        videos.push({
          id: track.track.id,
          stream: track.stream,
          kind: track.purpose === 'screen' ? 'screen' : 'camera',
          label: track.purpose === 'screen' ? 'Tela' : 'Câmera',
          ownerSessionId: participant.sessionId,
          ownerName: name,
          isLocal: false,
        });
      }
    }

    return videos;
  }, [voiceState, guildMembers]);

  /* ==========================================================================
     Render
     ========================================================================== */

  const inVoice = voiceState?.connected === true;

  /*
   * As quatro ações da conversa, montadas UMA vez.
   *
   * Elas saíram da barra superior e vieram para o cabeçalho da conversa. A barra
   * superior é da JANELA — mostra quem eu sou e o painel de administração — e um
   * "Desligar" vermelho ali fica ao lado do meu próprio avatar, longe da pessoa
   * com quem a chamada é. No cabeçalho da conversa ele fica a dois centímetros
   * do nome de quem vai ser desligado.
   */
  const dmActions =
    dmPeer === null || activeChannel === null ? null : (
      <div className="flex items-center" style={{ gap: 'var(--spacing-2)' }}>
        {!inVoice ? (
            <>
              <DmAction
                label="Voz"
                onClick={() => void joinVoice(activeChannel.id)}
              >
                <path d="M12 3.5a2.6 2.6 0 012.6 2.6v5a2.6 2.6 0 01-5.2 0v-5A2.6 2.6 0 0112 3.5z" />
                <path d="M5.5 10.5a6.5 6.5 0 0013 0M12 17v3.5M8.5 20.5h7" />
              </DmAction>

              <DmAction
                label="Vídeo"
                /*
                 * Vídeo entra na chamada E liga a câmera, nesta
                 * ordem. `toggleCamera` sem malha montada não tem
                 * par para quem enviar — devolveria silêncio.
                 */
                onClick={async () => {
                  await joinVoice(activeChannel.id);
                  await voiceRef.current?.toggleCamera();
                }}
              >
                <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
                <path d="M15.5 11l6-3.5v9l-6-3.5z" />
              </DmAction>

              <DmAction
                label="Compartilhar Tela"
                onClick={async () => {
                  await joinVoice(activeChannel.id);
                  await shareScreen();
                }}
              >
                <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
                <path d="M9 21h6M12 17v4" />
              </DmAction>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void leaveVoice()}
              className="flex items-center gap-2 px-4 py-2 font-semibold"
              style={{
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-danger-solid)',
                color: 'var(--color-on-solid)',
                /*
                 * MAIOR que os três de entrar, e não do mesmo tamanho.
                 *
                 * Encerrar é a única ação irreversível deste cabeçalho, e é a
                 * única que se procura com pressa — no meio de uma conversa, sem
                 * olhar. Um alvo maior é o que permite acertá-la sem mirar.
                 */
                fontSize: 'var(--text-xs)',
                /*
                 * Brilho vermelho: o botão de encerrar é o único
                 * irreversível deste cabeçalho, e a referência o
                 * destaca. Sem o brilho ele lê como mais um.
                 */
                boxShadow: '0 0 12px oklch(58% 0.210 25 / 0.45)',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" aria-hidden="true">
                <path d="M3 9.5c5-3.5 13-3.5 18 0l-2.5 3-3.5-1v-2c-2-.6-4-.6-6 0v2l-3.5 1L3 9.5z" />
              </svg>
              Desligar
            </button>
          )}
      </div>
    );

  /*
   * O palco de chamada só ocupa a área principal quando o canal selecionado É o
   * canal de voz em que a pessoa está — ou enquanto ela está entrando nele.
   */
  const voiceChannelId = voiceState?.channelId ?? joiningChannelId;

  /* ==========================================================================
     Canal de voz NUNCA vira canal de texto
     ==========================================================================
     A condição era só `activeChannelId === voiceChannelId` — ou seja, o palco
     dependia de a entrada na chamada ter DADO CERTO. Quando ela falhava, ou
     quando a malha caía, `voiceChannelId` virava nulo e o render caía no galho
     seguinte: `activeChannel !== null` → lista de mensagens e compositor.

     O resultado é o que apareceu na tela: a sala de voz "Voz" selecionada, a
     lateral listando duas pessoas dentro dela, e a área principal vazia com um
     campo escrito "Conversar em #Voz". Um canal de voz não tem conversa de
     texto, e oferecer um campo de mensagem ali é a interface afirmando que a
     falha não aconteceu.

     Agora o TIPO do canal decide. Se é canal de voz, é palco — conectado,
     conectando ou tendo falhado. O palco sabe mostrar cada um desses estados; o
     compositor de mensagem não sabe mostrar nenhum.
  ========================================================================== */
  const activeIsVoiceChannel =
    guildChannels.find((c) => c.id === activeChannelId)?.type === 'voice';

  const showVoiceStage =
    activeIsVoiceChannel ||
    (voiceChannelId !== null &&
      voiceChannelId !== undefined &&
      activeChannelId === voiceChannelId);

  /* ==========================================================================
     Telas que ocupam a janela inteira
     ==========================================================================
     Convite, configurações do servidor, preferências e administração. Elas eram
     `return` antecipados, e cada uma passava POR CIMA de tudo que vem no render
     principal — inclusive do `<VoiceAudio>`, que fica no fim dele.

     Desmontar o `<VoiceAudio>` desmonta os elementos `<audio>`, e desmontar um
     `<audio>` é, para o navegador, PARAR A REPRODUÇÃO. Era o relato: abrir
     Configurações durante a chamada mostrava você na chamada e você não ouvia
     mais ninguém — porque, para o áudio, você tinha mesmo saído.

     É a terceira vez que este erro aparece no projeto, sempre com a mesma
     forma: o áudio da chamada pendurado numa árvore que alguma navegação
     desmonta. O `VoiceAudio` já nasceu por causa das duas primeiras, e o
     comentário no topo dele diz a regra — "áudio de chamada não pode ser filho
     de uma tela". Um `return` antecipado é uma tela, mesmo sem parecer uma.

     Guardar em variável e decidir no fim é o que garante que o `<VoiceAudio>`
     seja renderizado nos dois caminhos.
  ========================================================================== */
  let telaCheia: React.ReactNode = null;

  /*
   * O convite vem ANTES de qualquer outra tela.
   *
   * Quem chegou por um link de convite tem uma unica intencao, e mostrar a tela
   * inicial primeiro (ou pior, o ultimo canal aberto) esconde o motivo de a
   * pessoa estar ali. Era exatamente o relato: o link abria o app na tela de
   * inicio, sem nenhum sinal de que havia um convite.
   */
  if (inviteCode !== null) {
    telaCheia = (
      <InviteScreen
        api={api}
        code={inviteCode}
        onJoined={(guildId) => {
          clearPendingInvite();
          setInviteCode(null);
          soundsRef.current?.play('join');
          /*
           * A lista e recarregada do servidor em vez de receber o servidor novo
           * por concatenacao.
           *
           * Aceitar um convite pode ter concedido cargos, e o `isOwner` e a
           * ordem da lista vem do servidor. Montar a entrada no cliente
           * produziria uma dock com um item incompleto ate o proximo
           * recarregamento.
           */
          void api
            .listGuilds()
            .then((list) => setGuilds(list))
            .catch(() => setError('Entrou no servidor, mas a lista nao atualizou.'));
          setRoute({ kind: 'guild', guildId });
          void loadGuildData(guildId);
        }}
        onDismiss={() => {
          clearPendingInvite();
          setInviteCode(null);
        }}
      />
    );
  /* ---- Telas de configuração ocupam a área principal inteira ---- */
  } else if (route.kind === 'guild-settings' && activeGuild !== null) {
    telaCheia = (
      <GuildSettings
        api={api}
        guild={activeGuild}
        channels={guildChannels}
        permissions={myPermissions}
        onClose={() => setRoute({ kind: 'guild', guildId: activeGuild.id })}
        onChanged={() => void loadGuildData(activeGuild.id)}
      />
    );
  } else if (route.kind === 'user-settings') {
    telaCheia = (
      <UserSettings
        voiceEngine={voiceRef.current}
        capabilities={capabilities}
        onClose={() => setRoute({ kind: 'home' })}
      />
    );
  } else if (route.kind === 'admin') {
    telaCheia = <AdminPanel api={api} onClose={() => setRoute({ kind: 'home' })} />;
  }

  /*
   * O áudio primeiro, e SEMPRE.
   *
   * Ele não é filho de rota nenhuma: enquanto houver chamada, os elementos de
   * áudio existem, esteja a janela mostrando a conversa, as configurações ou o
   * painel de administração.
   */
  if (telaCheia !== null) {
    return (
      <>
        {/* ----------------------------------------------------------------
            O MESMO `<VoiceAudio>` do outro caminho
            ----------------------------------------------------------------
            Escrito duas vezes no arquivo e UM só componente na tela, e a
            diferença é o que faz o conserto funcionar.

            React reconcilia por POSIÇÃO e tipo, não por qual `return` produziu
            o elemento. Nos dois caminhos o topo é um Fragment e o primeiro
            filho é um `<VoiceAudio>` — então, ao alternar entre eles, o
            reconciliador encontra o mesmo tipo na mesma posição, ATUALIZA a
            instância existente e não a desmonta. Os elementos `<audio>`
            sobrevivem, e com eles a reprodução.

            É por isso que ele precisa ser o PRIMEIRO filho nos dois. Colocado
            depois do conteúdo num dos caminhos, a posição deixaria de bater e o
            componente remontaria — reintroduzindo o corte de áudio que este
            conserto elimina.
        ---------------------------------------------------------------- */}
        <VoiceAudio
          state={voiceState}
          volumes={volumes}
          outputDeviceId={audioDevices.outputId}
          deafened={voiceState?.deafened ?? false}
          screenAudioMuted={screenAudioMuted}
        />
        {telaCheia}
      </>
    );
  }

  return (
    <>
      {/* ------------------------------------------------------------------
          Saida de audio da chamada
          ------------------------------------------------------------------
          IRMA do layout, nao filha dele. E o que faz a voz continuar tocando
          quando a pessoa abre um canal de texto durante a chamada: se estivesse
          dentro do palco, trocar de canal desmontaria os elementos de audio e
          pararia a reproducao. Ver o cabecalho de VoiceAudio.tsx.
      ------------------------------------------------------------------ */}
      <VoiceAudio
        state={voiceState}
        volumes={volumes}
        outputDeviceId={audioDevices.outputId}
        deafened={voiceState?.deafened ?? false}
        screenAudioMuted={screenAudioMuted}
      />
      <AppLayout
        dock={
          /*
           * O botao direito e capturado por um INVOLUCRO, nao por uma prop.
           *
           * A barra de comando vive em `packages/ui` e esta sendo reconstruida
           * em paralelo. Acrescentar uma prop la e depois consumi-la aqui
           * acoplaria as duas mudancas: se a barra fosse reescrita, este arquivo
           * pararia de compilar.
           *
           * O involucro depende apenas de cada botao de servidor carregar
           * `data-guild-id`, que e um atributo, nao API. `closest` sobe do alvo
           * do clique ate encontra-lo, o que funciona mesmo com o icone dentro
           * de um `<img>` ou `<span>` dentro do botao.
           */
          <div
            onContextMenu={(event) => {
              const target = (event.target as HTMLElement).closest<HTMLElement>(
                '[data-guild-id]',
              );
              const guildId = target?.dataset.guildId;
              if (guildId === undefined) return;
              event.preventDefault();
              setGuildMenu({ guildId, x: event.clientX, y: event.clientY });
            }}
          >
          <Dock
            guilds={dockGuilds}
            activeGuildId={activeGuildId}
            onSelectGuild={(guildId) => {
              setRoute({ kind: 'guild', guildId });
              if (channels[guildId] === undefined) void loadGuildData(guildId);
              const first = (channels[guildId] ?? []).find((c) => c.type === 'text');
              if (first) void openChannel(first.id);
            }}
            /*
             * Ir para o inicio LIMPA a conversa aberta.
             *
             * Sem isto, o botao de inicio devolvia a ultima conversa direta em
             * vez da lista de amigos -- e como o canal ativo era global, as vezes
             * devolvia um canal de servidor com o nome errado. "Amigos nao e um
             * canal": e o lugar de onde se escolhe com quem falar.
             */
            onOpenHome={() => {
              setRoute({ kind: 'friends' });
              setChannelByScope((current) => ({ ...current, home: null }));
            }}
            onOpenSettings={() => setRoute({ kind: 'user-settings' })}
            /*
             * O "+" abre um MENU, nao direto o formulario de criar.
             *
             * Faltava por completo o caminho de entrar num servidor existente
             * ("falta o botao de ingressar em um servidor e digitar o codigo"), e
             * as duas acoes pertencem ao mesmo lugar: quem clica em "+" quer ter
             * mais um servidor na dock, tanto faz se criando ou entrando.
             */
            onCreateGuild={() => {
              /*
               * A ancora sai do elemento com foco, nao de coordenadas de mouse.
               *
               * `onCreateGuild` nao recebe o evento, e usar o centro da tela
               * abriria o menu longe do botao. O elemento focado depois de um
               * clique E o botao -- e isso tambem faz o menu abrir no lugar certo
               * quando a acao vem do teclado.
               */
              const rect = (
                document.activeElement as HTMLElement | null
              )?.getBoundingClientRect();
              setAddServerMenu({
                x: rect ? rect.left : window.innerWidth / 2,
                y: rect ? rect.top - 8 : window.innerHeight / 2,
              });
            }}
            homeActive={route.kind === 'home' || route.kind === 'friends'}
            /* ---- Chip do usuário, na ponta direita da cápsula ---- */
            user={{
              id: user.id,
              displayName: user.displayName ?? user.username,
              username: user.username,
              status: 'online',
            }}
            onOpenProfile={() => setRoute({ kind: 'user-settings' })}
            onOpenAccountSettings={() => setRoute({ kind: 'user-settings' })}
            /*
             * A chamada. `null` encolhe a cápsula em vez de fazê-la piscar.
             *
             * `inVoice` e não `showVoiceStage`: os controles da chamada precisam
             * estar acessíveis mesmo quando a área principal mostra outro canal.
             * É o que permite ler o chat e continuar podendo mutar o microfone.
             */
            voice={
              inVoice && voiceState !== null
                ? {
                    channelName:
                      guildChannels.find((c) => c.id === voiceState.channelId)
                        ?.name ??
                      directChannels.find(
                        (d) => d.channelId === voiceState.channelId,
                      )?.name ??
                      'Chamada',
                    participantCount: voiceState.participants.length,
                    micEnabled: voiceState.micEnabled,
                    micLevel: audioLevel,
                    onToggleMic: toggleMic,
                    cameraEnabled: voiceState.cameraOn,
                    onToggleCamera: () => void voiceRef.current?.toggleCamera(),
                    screenCount: voiceState.screens.filter(
                      (s) => s.purpose === 'screen',
                    ).length,
                    maxScreens:
                      (myPermissions & Permission.SHARE_MULTI_SCREEN) !== 0n ? 4 : 1,
                    onShareScreen: () => void shareScreen(),
                    deafened: voiceState.deafened,
                    onToggleDeafen: () => voiceRef.current?.toggleDeafen(),
                    onOpenDevices: (
                      x: number,
                      y: number,
                      section: 'input' | 'output',
                    ) => setDevicePicker({ x, y, section }),
                    onLeave: () => void leaveVoice(),
                    uploadKbps: voiceState.bandwidth?.projectedKbps ?? 0,
                    budgetKbps: voiceState.bandwidth?.budgetKbps ?? 12_000,
                    notice: voiceState.bandwidth?.notice ?? null,
                  }
                : null
            }
          />
          </div>
        }
        topBar={
          <TopBar
            /*
             * Sem título fora de um servidor.
             *
             * "Início" saiu daqui — era o pedido, e estava errado por nomear um
             * lugar num produto cujo início é uma lista de pessoas. "Meus
             * amigos" também saiu: a coluna ao lado já diz "Amigos", e o mesmo
             * rótulo duas vezes na mesma linha horizontal não acrescenta nada.
             *
             * Numa conversa direta o nome vive no `ConversationHeader`, junto do
             * `@usuário`, do estado e dos botões de chamada — que é onde ele tem
             * companhia que o explica.
             */
            title={
              activeGuildId !== null
                ? (activeChannel?.name ?? activeGuild?.name ?? '')
                : ''
            }
            {...(activeChannel?.topic != null
              ? { subtitle: activeChannel.topic }
              : gatewayStatus !== 'ready'
                ? { subtitle: describeGatewayStatus(gatewayStatus) }
                : {})}
            /* O icone do servidor ao lado do nome do canal, como pedido. */
            {...(activeGuild !== null ? { guildName: activeGuild.name } : {})}
            {...(activeGuild?.iconUrl != null
              ? { guildIconUrl: activeGuild.iconUrl }
              : {})}
            user={{
              id: user.id,
              displayName: user.displayName ?? user.username,
              username: user.username,
              status: 'online',
              instanceAdmin: user.instanceAdmin,
            }}
            onOpenProfile={() => setRoute({ kind: 'user-settings' })}
            onLogout={onLogout}
            {...(activeGuildId !== null
              ? { onToggleAside: () => setAsideCollapsed((v) => !v) }
              : {})}
            asideCollapsed={asideCollapsed}
            actions={
              <>
                {user.instanceAdmin && (
                  <button
                    type="button"
                    onClick={() => setRoute({ kind: 'admin' })}
                    title="Painel de administração da instância"
                    className="px-2 py-1"
                    style={{
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-accent-subtle)',
                      color: 'var(--color-accent-on-subtle)',
                      fontSize: 'var(--text-2xs)',
                      fontWeight: 600,
                    }}
                  >
                    admin
                  </button>
                )}
                {activeGuildId !== null &&
                  (myPermissions & Permission.MANAGE_GUILD) !== 0n && (
                    <button
                      type="button"
                      onClick={() =>
                        setRoute({ kind: 'guild-settings', guildId: activeGuildId })
                      }
                      title="Configurações do servidor"
                      className="px-2 py-1"
                      style={{
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--text-2xs)',
                        color: 'var(--color-text-tertiary)',
                      }}
                    >
                      configurar
                    </button>
                  )}
              </>
            }
          />
        }
        sidebar={
          activeGuildId !== null ? (
            <Sidebar
              mode="guild"
              title={activeGuild?.name ?? 'Servidor'}
              channels={sidebarChannels}
              activeChannelId={activeChannelId}
              onSelectChannel={(channelId) => {
                const channel = guildChannels.find((c) => c.id === channelId);
                if (channel?.type === 'voice') {
                  void joinVoice(channelId);
                  return;
                }
                void openChannel(channelId);
              }}
              {...(activeGuild?.iconUrl != null
                ? { iconUrl: activeGuild.iconUrl }
                : {})}
              /*
               * O "+" agora existe nas DUAS secoes e diz QUAL tipo.
               *
               * Antes ele so aparecia em "Canais de texto" e levava para as
               * configuracoes do servidor -- onde nao havia formulario de criar
               * canal, porque a rota nem existia no servidor. Era o relato:
               * "cade a opcao de criar mais salas de voz?".
               */
              {...((myPermissions & Permission.MANAGE_CHANNELS) !== 0n
                ? {
                    onCreateChannel: (type: 'text' | 'voice') =>
                      void createChannelIn(activeGuildId, type),
                  }
                : {})}
            />
          ) : (
            <Sidebar
              mode="home"
              /*
               * "Amigos", nao "Inicio".
               *
               * "Inicio" nomeava um lugar e nao o conteudo — e o conteudo desta
               * coluna e uma lista de pessoas. O pedido foi direto: tirar
               * "Inicio" e deixar so a lista.
               */
              title="Amigos"
              friends={friends}
              /*
               * Os dois contadores saem do MAPA de relacoes, nao de `friends`.
               *
               * `friends` guarda so os aceitos, porque e o que a coluna lista.
               * Pedido pendente nao e amigo ainda, entao contar em cima de
               * `friends` daria zero sempre.
               */
              pendingIncoming={
                Object.values(friendship).filter((r) => r === 'incoming').length
              }
              pendingOutgoing={
                Object.values(friendship).filter((r) => r === 'outgoing').length
              }
              onOpenSettings={() => setRoute({ kind: 'user-settings' })}
              onSubmitFriendRequest={async (username) => {
                try {
                  await api.addFriend(username);
                  await reloadFriends();
                } catch (cause) {
                  soundsRef.current?.play('error');
                  setError(
                    cause instanceof Error && cause.message.length > 0
                      ? cause.message
                      : 'Não foi possível enviar o convite.',
                  );
                }
              }}
              activeChannelId={activeChannelId}
              /*
               * Clicar num amigo abre a CONVERSA, nunca a chamada.
               *
               * Era o relato: "esta entrando no modo call automaticamente ao
               * clicar em amigos". A causa nao era este handler -- era a
               * precedencia de render, em que estar em voz vencia tudo e o palco
               * cobria a conversa que acabava de abrir. Ligar e uma acao
               * separada e explicita, em `onCallFriend`.
               */
              onSelectFriend={async (userId) => {
                try {
                  const { channelId } = await api.openDirectChannel(userId);
                  setRoute({ kind: 'home' });
                  await openChannel(channelId);
                } catch {
                  setError('Não foi possível abrir a conversa.');
                }
              }}
              onOpenFriends={() => {
                setRoute({ kind: 'friends' });
                setChannelByScope((current) => ({ ...current, home: null }));
              }}
              onFriendContextMenu={(friendId, x, y) =>
                setFriendMenu({ friendId, x, y })
              }
            />
          )
        }
        aside={
          activeGuildId !== null && !asideCollapsed ? (
            <MemberList
              members={memberEntries}
              roles={guildRoles}
              viewer={{
                userId: user.id,
                roles:
                  memberEntries.find((m) => m.userId === user.id)?.roles ?? [],
                isOwner: activeGuild?.isOwner ?? false,
                permissions: myPermissions,
              }}
              voiceChannels={voiceChannelTargets}
              /*
               * Volume na lista de membros e chaveado por USUARIO; no palco, por
               * SESSAO. Sao escopos diferentes de proposito: na lista nao ha
               * sessao para falar de -- a pessoa pode nem estar em chamada.
               */
              volumes={volumes}
              onSetVolume={setParticipantVolume}
              onOpenProfile={() => undefined}
              onSendDirectMessage={async (userId) => {
                try {
                  const { channelId } = await api.openDirectChannel(userId);
                  setRoute({ kind: 'home' });
                  await openChannel(channelId);
                } catch {
                  setError('Não foi possível abrir a conversa.');
                }
              }}
              onAddFriend={async (userId) => {
                /*
                 * O pedido vai pelo NOME DE USUÁRIO, não pelo id.
                 *
                 * `POST /friends` recebe `username` — a rota foi desenhada para
                 * a tela de amigos, onde a pessoa digita o nome. Aqui o nome sai
                 * da própria lista de membros, então não há o que digitar.
                 */
                const member = guildMembers.find((m) => m.userId === userId);
                if (!member) return;
                try {
                  await api.addFriend(member.username);
                  await reloadFriends();
                } catch (cause) {
                  soundsRef.current?.play('error');
                  setError(
                    cause instanceof Error && cause.message.length > 0
                      ? cause.message
                      : 'Não foi possível enviar o convite de amizade.',
                  );
                }
              }}
              onAcceptFriend={async (userId) => {
                try {
                  await api.acceptFriend(userId);
                  await reloadFriends();
                } catch {
                  soundsRef.current?.play('error');
                  setError('Não foi possível aceitar o convite.');
                }
              }}
              onManageRoles={() =>
                setRoute({ kind: 'guild-settings', guildId: activeGuildId })
              }
              onToggleServerMute={async (userId, mute) => {
                try {
                  await api.updateMember(activeGuildId, userId, { serverMute: mute });
                  await loadGuildData(activeGuildId);
                } catch {
                  setError('Não foi possível silenciar este membro.');
                }
              }}
              onToggleServerDeafen={async (userId, deafen) => {
                try {
                  await api.updateMember(activeGuildId, userId, { serverDeaf: deafen });
                  await loadGuildData(activeGuildId);
                } catch {
                  setError('Não foi possível ensurdecer este membro.');
                }
              }}
              onMoveToVoiceChannel={async (userId, channelId) => {
                try {
                  await api.moveMember(activeGuildId, userId, channelId);
                  await loadGuildData(activeGuildId);
                } catch {
                  setError('Não foi possível mover este membro.');
                }
              }}
              onDisconnectFromVoice={async (userId) => {
                try {
                  await api.moveMember(activeGuildId, userId, null);
                  await loadGuildData(activeGuildId);
                } catch {
                  setError('Não foi possível desconectar este membro.');
                }
              }}
              onKick={async (userId) => {
                const member = guildMembers.find((m) => m.userId === userId);
                const name = member?.username ?? 'este membro';
                const proceed = await confirm({
                  title: `Expulsar ${name}?`,
                  description:
                    'Ele sai do servidor mas pode voltar com qualquer convite. ' +
                    'Para impedir que volte, use banimento.',
                  confirmLabel: 'Expulsar',
                  danger: true,
                });
                if (!proceed) return;
                try {
                  await api.kickMember(activeGuildId, userId);
                  await loadGuildData(activeGuildId);
                } catch {
                  setError('Não foi possível expulsar este membro.');
                }
              }}
              onBan={async (userId) => {
                const member = guildMembers.find((m) => m.userId === userId);
                const name = member?.username ?? 'este membro';
                /*
                 * Duas etapas: motivo e confirmação.
                 *
                 * O motivo vem primeiro porque escrevê-lo é o que faz a pessoa
                 * reconsiderar — e é o que permite revisar a decisão depois. Um
                 * banimento sem motivo registrado é indefensável em revisão.
                 */
                const reason = await ask({
                  title: `Banir ${name}`,
                  description:
                    'O membro sai e não poderá voltar, nem com convite. ' +
                    'O motivo aparece na lista de banidos.',
                  label: 'Motivo',
                  placeholder: 'Descumpriu as regras do servidor',
                  maxLength: 512,
                  confirmLabel: 'Continuar',
                });
                if (reason === null) return;
                const proceed = await confirm({
                  title: `Banir ${name}?`,
                  description: `Motivo: ${reason}`,
                  confirmLabel: 'Banir',
                  danger: true,
                });
                if (!proceed) return;
                try {
                  await api.banMember(activeGuildId, userId, {
                    ...(reason.length > 0 ? { reason } : {}),
                  });
                  await loadGuildData(activeGuildId);
                } catch {
                  setError('Não foi possível banir este membro.');
                }
              }}
            />
          ) : undefined
        }
        /*
         * O slot `voiceBar` NAO e mais usado.
         *
         * Havia duas barras na base da tela: a dock de servidores e, 8px acima
         * dela, a barra de chamada -- com alturas, raios e gramaticas de botao
         * diferentes. A referencia mostra UMA capsula, e ela agora e a
         * `CommandBar`, que recebe a chamada pela prop `voice`.
         *
         * Manter as duas montadas duplicaria os controles: dois botoes de
         * microfone, dois de encerrar. O slot continua existindo no `AppLayout`
         * para nao quebrar outro consumidor, mas este passou a nao preenche-lo.
         */
        asideCollapsed={asideCollapsed}
        onToggleAside={() => setAsideCollapsed((v) => !v)}
      >
        {/* ------------------------------------------------------------------
            Área principal
        ------------------------------------------------------------------ */}
        {route.kind === 'friends' ? (
          <FriendsScreen
            api={api}
            onOpenConversation={async (userId) => {
              const { channelId } = await api.openDirectChannel(userId);
              setRoute({ kind: 'home' });
              await openChannel(channelId);
            }}
            onFriendsChanged={reloadFriends}
          />
        ) : showVoiceStage ? (
          /*
           * O palco aparece quando o canal SELECIONADO é o da chamada.
           *
           * Duas condições erradas passaram por aqui, e vale registrar as duas:
           *
           *   `inVoice && stageVideos.length > 0` — entrar numa sala em que
           *   ninguém compartilha nada caía no estado "Escolha um canal", com a
           *   pessoa dentro da chamada.
           *
           *   `inVoice` sozinho — o palco passava a SEQUESTRAR a área
           *   principal: abrir uma conversa direta ou um canal de texto durante
           *   a chamada continuava mostrando a chamada.
           *
           * O certo é a chamada ser PERSISTENTE mas não exclusiva: a barra de
           * voz fica visível o tempo todo, e a área principal segue o canal
           * escolhido. É o que permite ler o chat enquanto se fala.
           */
          <VoiceStage
            /*
             * O nome sai do canal SELECIONADO, com o da chamada como reserva.
             *
             * Invertido — como estava — o palco de um canal de voz em que a
             * pessoa ainda não entrou se chamava "Chamada", um rótulo genérico
             * para uma sala que tem nome próprio na lateral logo ao lado.
             */
            channelName={
              guildChannels.find((c) => c.id === activeChannelId)?.name ??
              guildChannels.find(
                (c) => c.id === (voiceState?.channelId ?? joiningChannelId),
              )?.name ??
              'Chamada'
            }
            participants={stageParticipants}
            videos={stageVideos}
            deafened={voiceState?.deafened ?? false}
            onStopScreen={(sourceId) => void stopScreen(sourceId)}
            onSetVolume={setParticipantVolume}
            /*
             * `onJoin` só existe quando a pessoa NÃO está nesta chamada.
             *
             * É o que dá saída ao estado que antes não tinha nenhuma: sala
             * selecionada, gente dentro dela, e o cliente fora. Sem o botão, a
             * única forma de entrar era clicar no canal de novo — e o canal já
             * estava selecionado, então o clique não mudava rota e não fazia
             * nada. Era o beco sem saída relatado.
             */
            {...(activeChannelId !== null &&
            activeChannelId !== voiceState?.channelId &&
            joiningChannelId === null
              ? { onJoin: () => void joinVoice(activeChannelId) }
              : {})}
            onSetScreenAudioMuted={setScreenAudioMuted}
            screenAudioMuted={screenAudioMuted}
            notice={
              joiningChannelId !== null && !inVoice
                ? 'Conectando… se o navegador pedir acesso ao microfone, aceite para entrar na chamada.'
                : (voiceState?.notice ?? null)
            }
            onDismissNotice={() => undefined}
          />
        ) : activeChannel !== null ? (
          <div className="flex h-full min-h-0 flex-col">
            {/* ----------------------------------------------------------------
                Cabeçalho da conversa
                ----------------------------------------------------------------
                SÓ na conversa direta. Num canal de servidor o nome e o tópico já
                estão na barra superior ao lado do ícone do servidor, e um segundo
                cabeçalho repetiria os dois — dentro do painel que a barra
                acabou de rotular.

                O que justifica o cabeçalho aqui é o que ele tem e a barra não
                pode ter: o avatar da PESSOA, o `@usuário`, o estado dela e os
                botões de chamada. Nada disso existe para um `#canal`.
            ---------------------------------------------------------------- */}
            {dmPeer !== null && (
              <ConversationHeader
                name={dmPeer.name}
                seed={dmPeer.id}
                {...(dmPeer.username !== undefined
                  ? { username: dmPeer.username }
                  : {})}
                status={dmPeer.status}
                {...(dmActions !== null ? { actions: dmActions } : {})}
              />
            )}
            <MessageList
              messages={messages[activeChannel.id] ?? []}
              currentUserId={user.id}
              currentUserRoleIds={myRoleIds}
              channelName={dmPeer?.name ?? activeChannel.name}
              channelTopic={activeChannel.topic}
              /*
               * `direct` liga as bolhas e tira o `#` do marco de início.
               *
               * A condição é `dmPeer !== null` e não `activeGuildId === null`:
               * fora de um servidor também se cai aqui com um canal que não é
               * conversa de duas pessoas, e ali o formato de canal é o certo.
               */
              direct={dmPeer !== null}
              loadingOlder={loadingOlder}
              hasMore={hasMore[activeChannel.id] ?? true}
              onLoadOlder={() => void loadOlder()}
              typing={typingByChannel[activeChannel.id] ?? []}
              pins={pinsByChannel[activeChannel.id] ?? []}
              /*
               * A acao de fixar e OMITIDA quando falta permissao, nao
               * desabilitada.
               *
               * Foi o pedido: "so admins ou cargos elegiveis no servidor podem
               * fixar". Um botao cinza no hover de toda mensagem seria ruido
               * permanente para quem nunca podera usa-lo. Em conversa direta
               * `activeGuildId` e nulo e as duas pontas podem fixar -- nao ha
               * moderacao numa conversa de dois.
               */
              {...(activeGuildId === null ||
              (myPermissions & Permission.MANAGE_MESSAGES) !== 0n
                ? {
                    onTogglePin: (messageId: string, pinned: boolean) =>
                      void togglePin(messageId, pinned),
                  }
                : {})}
            />
            <Composer
              /*
               * O aviso de digitação, finalmente ligado.
               *
               * O `Composer` já tinha `onTyping` e já o estrangulava a um envio
               * a cada 3 segundos; o `MessageList` já desenhava a linha "está
               * digitando"; o `AppShell` já escutava `TYPING_START`. Faltavam
               * duas pontas: o opcode no protocolo e ESTA prop. Três quartos da
               * funcionalidade estavam prontos em volta de um buraco.
               */
              onTyping={() => {
                gatewayRef.current?.sendTyping(activeChannel.id);
              }}
              channelName={dmPeer?.name ?? activeChannel.name}
              direct={dmPeer !== null}
              rateLimitSeconds={activeChannel.rateLimitSeconds}
              disabled={
                activeGuildId !== null &&
                (myPermissions & Permission.SEND_MESSAGES) === 0n
              }
              disabledReason="Você não tem permissão para escrever neste canal."
              onSend={sendMessage}
              /*
               * Anexos DESABILITADOS com explicação, e não escondidos.
               *
               * O armazenamento de objetos está desligado por falta de memória no
               * servidor. Esconder o botão faria parecer que o produto não tem a
               * função; desabilitar com o motivo diz o que está faltando.
               */
              attachmentsEnabled={false}
              attachmentsDisabledReason={
                'Anexos indisponíveis: o armazenamento de arquivos não está ' +
                'configurado nesta instância.'
              }
              onAttach={() => undefined}
            />
          </div>
        ) : activeGuildId === null ? (
          /*
           * Sem servidor selecionado e sem conversa aberta, o inicio E a tela de
           * amigos.
           *
           * O "Escolha um canal" ficava aqui, e era errado por dois motivos.
           * Primeiro porque nao havia canal para escolher -- a lateral do inicio
           * lista pessoas, nao canais. Segundo porque, como o usuario disse,
           * "amigos nao e um canal, e onde voce pode selecionar um amigo para
           * trocar mensagem ou entrar em call": a lista de amigos e o destino, e
           * nao um desvio a partir de um estado vazio.
           */
          <FriendsScreen
            api={api}
            onOpenConversation={async (userId) => {
              const { channelId } = await api.openDirectChannel(userId);
              setRoute({ kind: 'home' });
              await openChannel(channelId);
            }}
            onFriendsChanged={reloadFriends}
          />
        ) : (
          <EmptyState
            hasGuilds={guilds.length > 0}
            onOpenFriends={() => setRoute({ kind: 'friends' })}
          />
        )}
      </AppLayout>


      {/* ---- Menus flutuantes ---- */}
      {addServerMenu !== null && (
        <AddServerMenu
          at={addServerMenu}
          onClose={() => setAddServerMenu(null)}
          onCreate={async () => {
            const name = await ask({
              title: 'Criar servidor',
              description:
                'Um canal de texto e uma sala de voz são criados junto. Você entra como dono.',
              label: 'Nome',
              placeholder: 'Time de produto',
              maxLength: 100,
              confirmLabel: 'Criar',
              validate: (value) =>
                value.trim().length < 2
                  ? 'O nome precisa de ao menos 2 caracteres.'
                  : null,
            });
            if (name === null) return;
            try {
              const guild = await api.createGuild({ name });
              setGuilds((current) => [...current, guild]);
              setRoute({ kind: 'guild', guildId: guild.id });
              void loadGuildData(guild.id);
            } catch {
              soundsRef.current?.play('error');
              setError('Não foi possível criar o servidor.');
            }
          }}
          onJoin={() => void askForInviteCode()}
        />
      )}

      {guildMenu !== null &&
        (() => {
          const guild = guilds.find((g) => g.id === guildMenu.guildId);
          if (!guild) return null;
          /*
           * As permissoes do menu vem do que ja foi carregado, com fallback
           * generoso para o DONO.
           *
           * `permissions[guildId]` so existe depois de `loadGuildData`, e o
           * botao direito pode acontecer num servidor que a pessoa nunca abriu.
           * Esconder as acoes nesse caso faria o menu do proprio dono aparecer
           * vazio -- e o servidor ainda autoriza de verdade, entao mostrar a
           * acao e no pior caso um erro explicativo.
           */
          const known = permissions[guild.id];
          const perms = known ?? (guild.isOwner || user.instanceAdmin ? ~0n : 0n);

          return (
            <GuildContextMenu
              at={{ x: guildMenu.x, y: guildMenu.y }}
              guildName={guild.name}
              isOwner={guild.isOwner}
              canManage={(perms & Permission.MANAGE_GUILD) !== 0n}
              canCreateInvite={(perms & Permission.CREATE_INVITE) !== 0n}
              canManageChannels={(perms & Permission.MANAGE_CHANNELS) !== 0n}
              onClose={() => setGuildMenu(null)}
              onChangeIcon={() => void pickGuildImage(guild.id, 'icon')}
              onChangeBanner={() => void pickGuildImage(guild.id, 'banner')}
              onOpenSettings={() => {
                setRoute({ kind: 'guild-settings', guildId: guild.id });
                if (channels[guild.id] === undefined) void loadGuildData(guild.id);
              }}
              onCreateInvite={() => void createShareCode(guild.id)}
              onCreateChannel={(type) => void createChannelIn(guild.id, type)}
              onLeave={async () => {
                const proceed = await confirm({
                  title: `Sair de ${guild.name}?`,
                  description:
                    'Você perde o acesso aos canais e aos cargos. Para voltar, ' +
                    'precisará de um convite novo.',
                  confirmLabel: 'Sair do servidor',
                  danger: true,
                });
                if (!proceed) return;
                try {
                  await api.leaveGuild(guild.id);
                  setGuilds((current) => current.filter((g) => g.id !== guild.id));
                  setRoute({ kind: 'friends' });
                } catch {
                  soundsRef.current?.play('error');
                  setError('Não foi possível sair deste servidor.');
                }
              }}
            />
          );
        })()}

      {devicePicker !== null && (
        <DevicePicker
          x={devicePicker.x}
          y={devicePicker.y}
          onClose={() => setDevicePicker(null)}
          inputId={audioDevices.inputId}
          onSelectInput={(id, label) => void selectAudioDevice('input', id, label)}
          outputId={audioDevices.outputId}
          onSelectOutput={(id, label) => void selectAudioDevice('output', id, label)}
          /*
           * `setSinkId` só existe no Chromium. Onde não existe, a seção de saída
           * explica em vez de oferecer um seletor que não faz nada.
           */
          canSelectOutput={capabilities.canSelectOutputDevice}
          sections={devicePicker.section}
        />
      )}

      {friendMenu !== null &&
        (() => {
          const friend = friends.find((f) => f.id === friendMenu.friendId);
          if (!friend) return null;
          return (
            <FriendContextMenu
              at={{ x: friendMenu.x, y: friendMenu.y }}
              name={friend.name}
              username={friend.username}
              muted={mutedFriends.has(friend.id)}
              onClose={() => setFriendMenu(null)}
              onOpenConversation={async () => {
                try {
                  const { channelId } = await api.openDirectChannel(friend.id);
                  setRoute({ kind: 'home' });
                  await openChannel(channelId);
                } catch {
                  setError('Não foi possível abrir a conversa.');
                }
              }}
              onCall={() => void callFriend(friend.id)}
              onToggleMute={() => toggleFriendMute(friend.id)}
              onRemove={async () => {
                const proceed = await confirm({
                  title: `Remover ${friend.name}?`,
                  description:
                    'Vocês deixam de ser amigos. O histórico da conversa ' +
                    'permanece, e qualquer um dos dois pode enviar um novo ' +
                    'pedido depois.',
                  confirmLabel: 'Remover amizade',
                  danger: true,
                });
                if (!proceed) return;
                try {
                  await api.removeFriend(friend.id);
                  await reloadFriends();
                } catch {
                  soundsRef.current?.play('error');
                  setError('Não foi possível remover esta amizade.');
                }
              }}
              onBlock={async () => {
                const proceed = await confirm({
                  title: `Bloquear ${friend.name}?`,
                  description:
                    'Ele não poderá te enviar mensagens nem te chamar. Você ' +
                    'continua vendo o histórico — apagá-lo destruiria a prova ' +
                    'do motivo do bloqueio.',
                  confirmLabel: 'Bloquear',
                  danger: true,
                });
                if (!proceed) return;
                try {
                  await api.blockUser(friend.id);
                  await reloadFriends();
                } catch {
                  soundsRef.current?.play('error');
                  setError('Não foi possível bloquear esta pessoa.');
                }
              }}
            />
          );
        })()}

      {generatedInvite !== null && (
        <InviteCodePanel
          url={generatedInvite.url}
          code={generatedInvite.code}
          expiresAt={generatedInvite.expiresAt}
          maxUses={generatedInvite.maxUses}
          onClose={() => setGeneratedInvite(null)}
        />
      )}

      {error !== null && <ErrorToast message={error} onDismiss={() => setError(null)} />}
    </>
  );
}

/* ==============================================================================
   Auxiliares
============================================================================== */

function toChatMessage(message: {
  id: string;
  channelId: string;
  content: string | null;
  createdAt: string;
  editedAt: string | null;
  author: {
    id: string;
    username: string;
    displayName: string | null;
    nickname: string | null;
  } | null;
}): ChatMessage {
  return {
    id: message.id,
    channelId: message.channelId,
    content: message.content,
    createdAt: message.createdAt,
    editedAt: message.editedAt,
    author: message.author,
  };
}

function describeGatewayStatus(status: string): string {
  switch (status) {
    case 'connecting':
      return 'Conectando…';
    case 'identifying':
      return 'Autenticando…';
    case 'reconnecting':
      return 'Reconectando… as chamadas em andamento continuam.';
    case 'closed':
      return 'Desconectado do tempo real.';
    default:
      return '';
  }
}

function EmptyState({
  hasGuilds,
  onOpenFriends,
}: {
  hasGuilds: boolean;
  onOpenFriends: () => void;
}) {
  return (
    <div className="grid h-full place-items-center px-6">
      <div className="max-w-sm text-center">
        <h2
          className="font-bold"
          style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--color-text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          {hasGuilds ? 'Escolha um canal' : 'Comece por aqui'}
        </h2>
        <p
          className="mt-2"
          style={{
            fontSize: 'var(--text-sm)',
            lineHeight: 'var(--leading-relaxed)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {hasGuilds
            ? 'Selecione um canal de texto para conversar, ou um canal de voz para entrar numa chamada com compartilhamento de tela.'
            : 'Crie um servidor na dock abaixo, ou adicione um amigo para conversar diretamente.'}
        </p>
        {!hasGuilds && (
          <button
            type="button"
            onClick={onOpenFriends}
            className="mt-4 px-4 py-2 font-medium"
            style={{
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-xs)',
            }}
          >
            Adicionar um amigo
          </button>
        )}
      </div>
    </div>
  );
}

function ErrorToast({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  /*
   * Desaparece sozinho em 8 segundos.
   *
   * Erro que exige clique para fechar acumula na tela durante uma sessão longa.
   * 8s é tempo de ler uma frase sem obrigar a interação — e o botão continua ali
   * para quem quer fechar antes.
   */
  useEffect(() => {
    const timer = setTimeout(onDismiss, 8000);
    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  return (
    <div
      role="alert"
      className="fixed left-1/2 flex max-w-md -translate-x-1/2 items-start gap-3 px-4 py-3"
      style={{
        top: 12,
        zIndex: 'var(--z-toast)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface-overlay)',
        boxShadow:
          'var(--glass-edge), 0 16px 48px oklch(0% 0 0 / 0.5), var(--glow-medium)',
      }}
    >
      <span
        aria-hidden="true"
        className="mt-1 block size-2 shrink-0 rounded-full"
        style={{ background: 'var(--color-danger-solid)' }}
      />
      <p
        className="min-w-0 flex-1"
        style={{
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-normal)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {message}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fechar"
        style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}
      >
        ✕
      </button>
    </div>
  );
}

/* ==============================================================================
   Ação do cabeçalho de conversa direta
   ==============================================================================
   Ícone em cima, rótulo embaixo — a MESMA gramática dos controles da barra de
   comando. Repetir a forma é deliberado: um botão de chamada deve ser
   reconhecível pela silhueta, e trocar o formato entre cabeçalho e barra faria a
   pessoa reler para ter certeza do que está apertando.
============================================================================== */

function DmAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void | Promise<void>;
  children: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex flex-col items-center justify-center px-2.5 py-1 transition-colors"
      style={{
        minWidth: 56,
        borderRadius: 'var(--radius-md)',
        background: hover ? 'oklch(100% 0 0 / 0.07)' : 'transparent',
        color: hover ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
      }}
    >
      <svg
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {children}
      </svg>
      <span
        className="mt-0.5 whitespace-nowrap leading-none"
        style={{ fontSize: 'var(--text-2xs)' }}
      >
        {label}
      </span>
    </button>
  );
}
